import type { ContentIssue } from '@/types/contentValidation';
import type { NarrativeBasis } from '@/types/contentNarrative';
import type { WizardData, WizardDraft, WizardStep, WizardValue } from '@/types/contentWizard';
import { queryClient } from './queryClient';
import { auth } from './firebase';
import { appCheckHeader } from './appCheck';
import { addBreadcrumb } from './breadcrumbs';
import { notifyCommunityLocked } from './emailVerification';
import { getLocale, getT, getUnitSystem } from '../i18n';

/** A 7 napos türelmi idő utáni közösségi zárolás hibakódja (`server/src/lib/communityLock.ts`). */
export const COMMUNITY_LOCKED = 'community_locked';
import type { PerfHistoryEntry } from './perfMeter';
import type { ActivityType, RouteManeuver, Visibility } from '@/types';
import type { LiveBattlePlayer } from '@/config/liveBattle';
import type { KnowledgeContext, KnowledgeData, KnowledgeKind, KnowledgeRecord, KnowledgeIssue } from '@/types/contentKnowledge';
import type { NarrativeView, NarrativeKind, NarrativeSave, NarrativeVersion, NarrativeApprove, NarrativeApproval, NarrativeHistory } from '@/types/contentNarrative';
import type { PlanningSetup, PlanningRequest, PlanningRun } from '@/types/contentPlanning';
import type { ProductionBrief, ProductionBriefRequest, ProductionDraftRequest, ProductionDraftResult } from '@/types/contentProduction';

/**
 * A GRUNDO backend kliense.
 *
 * MINDEN játékadat-írás ezen megy keresztül — a kliens Firestore-ba
 * közvetlenül csak a saját, engedélyezett mezőit írhatja (firestore.rules).
 */

/**
 * Emulátoros módban a backend MINDIG a helyi szerver.
 *
 * ⚠️ Ez nem kényelmi döntés, hanem védőháló. A Vite sorrendjében a `.env.local`
 * előbbre van, mint a `.env.emulator`, és a `.env.local`-ban az ÉLES Cloud Run
 * cím áll — vagyis emulátoros felületről az éles backendre mennének az írások,
 * miközben a fejlesztő azt hiszi, hogy homokozóban játszik. A mód és a
 * backend együtt jár: ha emulátor, akkor helyi szerver.
 */
/**
 * A helyi backend AZON A GÉPEN fut, ahonnan az oldal jött — nem „localhost"-on.
 *
 * Beégetett `localhost` mellett a fejlesztői felület csak a futtató gépen
 * működött. Telefonról megnyitva (`http://192.168.x.y:5173`) a `localhost`
 * magát a telefont jelentette volna, és minden API-hívás elhalt. A
 * `location.hostname` asztali gépen ugyanúgy `localhost`, tehát a korábbi
 * viselkedés nem változik.
 */
const EMULATOR_API_BASE = `http://${typeof location === 'undefined' ? 'localhost' : location.hostname}:8080`;

export const API_BASE = (
  import.meta.env.VITE_USE_EMULATORS === '1'
    ? EMULATOR_API_BASE
    : (import.meta.env.VITE_API_BASE_URL ?? '')
).replace(/\/+$/, '');

/**
 * A saját eredetére mutató API-cím NEM backend.
 *
 * Két okból kell ezt külön kezelni. Egyrészt könnyű elrontani: a Cloud Run
 * konzolban a frontend URL-je hasonlóan néz ki, mint a backendé. Másrészt az
 * AI Studio értéket vár minden felismert környezeti változóhoz — üresen nem
 * indul el az app —, tehát a „még nincs backend" állapotot nem lehet üres
 * stringgel kifejezni.
 *
 * Ha az API-cím a saját eredetünkre mutat, úgy tekintjük, hogy nincs backend:
 * az app működik tovább, profil nélkül, hibaüzenetek nélkül.
 */
function pointsAtSelf(base: string): boolean {
  if (typeof location === 'undefined') return false;
  try {
    return new URL(base, location.href).origin === location.origin;
  } catch {
    return false;
  }
}

const selfReferencing = API_BASE.length > 0 && pointsAtSelf(API_BASE);

if (selfReferencing) {
  // eslint-disable-next-line no-console
  console.warn(
    `[GRUNDO] A VITE_API_BASE_URL (${API_BASE}) a frontend saját címére mutat, ` +
      'nem a Cloud Run backendre. Az app backend nélküli módban fut.',
  );
}

export const apiConfigured = API_BASE.length > 0 && !selfReferencing;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /** Hány másodperc múlva próbálható újra (429-nél, ha a szerver megadta). */
    public readonly retryAfter?: number,
    public readonly details?: { fields: ContentIssue[] },
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type ApiErrorFallback = 'requestFailed' | 'imageLoadFailed';

/**
 * Local transport errors have stable client codes, so translate those here.
 * Unambiguous server codes also translate locally, including with an older backend.
 * Context-dependent and unknown codes keep the request-localized server message.
 */
export function apiErrorMessage(
  code: string,
  serverMessage?: string,
  fallback: ApiErrorFallback = 'requestFailed',
): string {
  const { apiErrors } = getT();
  switch (code) {
    case 'api_unconfigured':
      return apiErrors.apiUnconfigured;
    case 'network':
      return apiErrors.network;
    case 'not_json':
      return apiErrors.notJson;
    case 'empty_response':
      return apiErrors.emptyResponse;
    case 'not_image':
      return apiErrors.notImage;
    default:
      return (getT().apiServerErrors as Record<string, string>)[code] ?? serverMessage ?? apiErrors[fallback];
  }
}

/**
 * A bejelentkezett felhasználó ID tokenje, vagy `null`.
 *
 * ⚠️ HIDEGINDÍTÁSKOR A BEJELENTKEZÉS MÉG NINCS VISSZAÁLLÍTVA. A Firebase a
 * mentett munkamenetet aszinkron tölti vissza; addig a `currentUser` `null`,
 * és a kérés token NÉLKÜL menne ki. Mért eset (2026-09-24, iOS, Cloud Run
 * napló): az app újranyitásakor a félbemaradt mentés állapot-lekérdezése
 * azonnal elindult, a szerver kétszer 401-et adott („Hiányzó azonosítás."),
 * a kliens pedig végleges hibának vette — a 120 km-es, valójában sikeresen
 * elmentett futás „A mentés nem sikerült" üzenettel ragadt be. Az
 * `authStateReady()` a visszaállítás végét várja meg; utána azonnal teljesül,
 * tehát a normál kéréseket nem lassítja.
 */
async function currentIdToken(): Promise<string | null> {
  if (!auth) return null;
  await auth.authStateReady();
  const user = auth.currentUser;
  return user ? user.getIdToken() : null;
}

/**
 * Exportálva: az admin saját API-modulja (`src/admin/alfaApi.ts`) ezt hívja,
 * hogy a hívásai az admin lusta chunkjában maradjanak, ne a fő bundle-ben.
 */
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  if (!apiConfigured) {
    throw new ApiError(0, 'api_unconfigured', apiErrorMessage('api_unconfigured'));
  }

  const token = await currentIdToken();

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(await appCheckHeader()),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError(0, 'network', apiErrorMessage('network'));
  }

  /**
   * A válasznak JSON-nak KELL lennie.
   *
   * Ha a VITE_API_BASE_URL véletlenül a frontendre mutat, a statikus tárhely
   * minden ismeretlen útvonalra az index.html-t adja vissza — 200-as
   * státusszal, HTML tartalommal. Enélkül az ellenőrzés nélkül ebből egy
   * érthetetlen TypeError lenne a profil betöltésénél; így viszont megmondjuk,
   * mi a baj.
   */
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new ApiError(
      response.status,
      'not_json',
      apiErrorMessage('not_json'),
    );
  }

  const body = (await response.json().catch(() => null)) as
    | { code?: string; message?: string; retryAfter?: number; details?: { fields: ContentIssue[] } }
    | null;

  if (!response.ok) {
    /**
     * A hibás válasz morzsát is hagy a hibabejelentéshez.
     *
     * ⚠️ CSAK a státusz, a kód és az ÚTVONAL — a válasz törzse soha. Abban
     * felhasználónév, e-mail és tokenszerű mező is lehet, a bugreport pedig
     * kikerül a készülékről. A hívás debug módon kívül no-op
     * (`src/lib/breadcrumbs.ts`).
     */
    addBreadcrumb('api', `${response.status} ${body?.code ?? 'unknown'} ${path}`);
    if (body?.code === COMMUNITY_LOCKED) notifyCommunityLocked();
    throw new ApiError(
      response.status,
      body?.code ?? 'unknown',
      apiErrorMessage(body?.code ?? 'unknown', body?.message),
      typeof body?.retryAfter === 'number' ? body.retryAfter : undefined,
      body?.details,
    );
  }

  if (body === null) {
    throw new ApiError(response.status, 'empty_response', apiErrorMessage('empty_response'));
  }
  if (init?.method && init.method !== 'GET' && !path.startsWith('/api/messages') && path !== '/api/auth/locale') {
    // Mutations can affect activity visibility, authors, reactions or statistics.
    // Private messages and email-language metadata cannot change activity feeds.
    void queryClient.invalidateQueries({ queryKey: ['activities'] });
  }
  return body as T;
}

/**
 * Hitelesített bináris válasz — az aktivitásfotók nem publikus URL-ek.
 * `kind: 'file'`: tetszőleges letölthető fájl (CONNECTOR-export, #118), képtípus-ellenőrzés nélkül.
 */
async function requestBlob(
  path: string,
  signal?: AbortSignal,
  jsonBody?: unknown,
  kind: 'image' | 'file' = 'image',
): Promise<Blob> {
  if (!apiConfigured) {
    throw new ApiError(0, 'api_unconfigured', apiErrorMessage('api_unconfigured'));
  }

  const token = await currentIdToken();

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      signal,
      // An image produced from a request body (the share image, #104) is a POST.
      ...(jsonBody !== undefined ? { method: 'POST', body: JSON.stringify(jsonBody) } : {}),
      headers: {
        Accept: kind === 'image' ? 'image/*' : '*/*',
        ...(jsonBody !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(await appCheckHeader()),
      },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'network', apiErrorMessage('network'));
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { code?: string; message?: string }
      | null;
    throw new ApiError(
      response.status,
      body?.code ?? 'unknown',
      apiErrorMessage(body?.code ?? 'unknown', body?.message, kind === 'image' ? 'imageLoadFailed' : 'requestFailed'),
    );
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (kind === 'image' && !contentType.startsWith('image/')) {
    throw new ApiError(response.status, 'not_image', apiErrorMessage('not_image'));
  }
  return response.blob();
}

/**
 * A KÉRÉS TÖRZSÉNEK GZIP-TÖMÖRÍTÉSE — nagy nyomvonalaknál.
 *
 * ⚠️ Miért itt, és nem mindenütt? A `fetch` a KÉRÉS törzsét sosem tömöríti
 * magától (a VÁLASZ igen — azt a szerver és a böngésző intézi egymás közt).
 * A legtöbb GRUNDO-kérés pár száz bájt, ott a tömörítés rezsije több kárt
 * okozna, mint hasznot. Egy hosszú aktivitás nyomvonala viszont ismétlődő
 * lat/lng/t hármasokból áll — Jamal 12 órás menete (2026-09-01) 1,1 MB nyers
 * JSON volt, ami mobilneten önmagában 10-30 másodperc feltöltés, MÉG a
 * szerveroldali feldolgozás előtt.
 *
 * A szerver oldalon NEM KELL semmit tenni: az `express.json()` a
 * body-parseren keresztül alapból dekódolja a `Content-Encoding: gzip`
 * fejlécű kéréseket (`inflate` alapból bekapcsolt).
 *
 * ⚠️ A `CompressionStream` iOS Safari/WKWebView-n csak 16.4-től létezik.
 * Ahol nincs (vagy bármi közben elhasal), a sima JSON megy tömörítés
 * nélkül — ez a régi, működő viselkedés, nem hibaágazat. A tömörítés soha
 * nem állíthatja meg a mentést.
 */
async function compressedJsonBody(
  payload: unknown,
): Promise<{ body: BodyInit; headers: Record<string, string> }> {
  const json = JSON.stringify(payload);
  if (typeof CompressionStream === 'undefined') {
    return { body: json, headers: {} };
  }
  try {
    const stream = new Blob([json]).stream().pipeThrough(new CompressionStream('gzip'));
    const body = await new Response(stream).blob();
    return { body, headers: { 'Content-Encoding': 'gzip' } };
  } catch {
    return { body: json, headers: {} };
  }
}

/* ═══════════════════════════════════════════════════════════════════
   Típusok — a szerver által visszaadott profil (docs/05)
   ═══════════════════════════════════════════════════════════════════ */

export interface PersonalStatsResult {
  type: 'run' | 'walk' | 'ride';
  activities: number;
  distanceM: number;
  movingS: number;
  longestM: number;
  avgPaceSPerKm: number | null;
  avgSpeedKmh: number | null;
  bestEfforts: { km: number; timeS: number }[];
  grundo: {
    areaM2: number;
    stolenCells: number;
    breakthroughCells: number;
    gp: { total: number; base: number; claim: number; steal: number; breakthrough: number; other: number };
  };
  /** Igaz, ha a legújabb 5000 aktivitásnál régebbiek kimaradtak. */
  truncated: boolean;
}

export interface Profile {
  uid: string;
  /** A megjelenítési alak, ahogy a felhasználó beírta — „Geri". */
  username: string;
  /** Az egyediségi kulcs — „geri". Kereséshez, névfeloldáshoz, hivatkozáshoz. */
  usernameLower: string;
  displayName: string;
  /** Short self-description shown on the public profile; absent on old profiles. */
  bio?: string;
  /** ISO 3166-1 alpha-2 („HU"); `null`/absent = not given (#96). */
  countryCode?: string | null;
  email: string;
  emailVerified: boolean;
  photoURL: string | null;
  level: number;
  gpTotal: number;
  gpWeek: number;
  territoryM2: { foot: number; bike: number };
  /** Hány meződ van rétegenként — a profil összesítése, nem a látott szakaszé. */
  cellCount: { foot: number; bike: number };
  zoneCount: { foot: number; bike: number };
  streak: {
    current: number;
    longest: number;
    /** Hány LEZÁRT hét óta tart a heti sorozat. A folyó hét még nincs benne. */
    weeks: number;
    /**
     * Ebben a folyó hétben eddig ennyi AKTÍV NAP volt.
     *
     * ⚠️ NAP, NEM AKTIVITÁS. Három futás ugyanazon a napon egyetlen aktív nap
     * — a heti sorozat feltétele `GAMEPLAY.WEEK_STREAK_MIN_ACTIVE_DAYS`
     * KÜLÖNBÖZŐ napon végzett mozgás. A számlálót a `dailyRollover` vezeti,
     * és hétzáráskor ugyanez a szám dönt a sorozat sorsáról.
     *
     * Régi kliensnek/dokumentumnak hiányozhat, ezért opcionális.
     */
    weekActiveDays?: number;
  };
  counters: {
    activities: number;
    followers: number;
    following: number;
    distanceKm: { run: number; walk: number; ride: number };
  };
  pro: { active: boolean };
  /**
   * Tesztelői kör tagja-e. Ettől függ, megjelenik-e az üzemmód-választó és a
   * lebegő hibabejelentő gomb. KIZÁRÓLAG adminból állítható
   * (`POST /api/admin/testers`) — kliensről állítható tesztelői jog azt
   * jelentené, hogy a debug felület bárkinek elérhető.
   */
  tester?: boolean;
  /**
   * A választott cellaszín KULCSA — lásd `src/lib/cellColors.ts`.
   *
   * Hiányozhat: aki még nem választott, az alapértelmezett színt kapja. A
   * hexkód szándékosan NEM tárolódik, hogy a paletta finomhangolása ne
   * igényeljen adatmigrációt.
   */
  cellColor?: string;
  privacy: {
    hideStart: boolean;
    startRadiusM: 50 | 100 | 200;
    hideEnd: boolean;
    endRadiusM: 50 | 100 | 200;
    routeRevision: number;
    /**
     * Nyilvános vagy privát fiók (terv 1.3). Privátnál a közösségi tartalmat
     * csak elfogadott követő látja. Hiányzik, ha még sosem állította —
     * akkor nyilvános.
     */
    account?: 'public' | 'private';
    /**
     * Mikor mentette a felhasználó ELŐSZÖR a privát zóna beállítását (ms),
     * vagy `null`, ha még soha — ilyenkor az onboarding kötelező lépése
     * (`docs/02` → Privát zóna) még nem futott le, és az első rögzítés-
     * indítás elé kell állítani (`Dock.tsx`).
     */
    privacyZoneSetAt: number | null;
    /** Visibility of NEW activities; missing on old profiles → `everyone`. */
    defaultVisibility?: Visibility;
  };
  units: { distance: 'km' | 'mi'; weight: 'kg' | 'lbs'; height: 'cm' | 'ftin' };
  /** Testadatok — mind opcionális, átugorható onboarding-mező (`docs/02:26`). */
  body: { weightKg?: number; heightCm?: number; dob?: string };
  /**
   * Név nélkül jön — csak `{id, earnedAt}`. A nevet, leírást és ritkasági
   * színt a `src/game/badges.ts` katalógusból oldjuk fel, ami a szerverrel
   * közös kódban él.
   */
  badges: EarnedBadge[];
  /**
   * A folyamatban lévő fióktörlés állapota, vagy `null` — a Beállítások →
   * Jogi → „Fiók törlése" képernyő és a türelmi idő alatti belépéskor
   * megjelenő „Visszavonod?" képernyő ebből dolgozik. Lásd `docs/05` →
   * Fióktörlés folyamata.
   */
  deletion: AccountDeletionState | null;
  /** A legutóbbi adatexport-kérés állapota, vagy `null`. */
  dataExport: DataExportState | null;
}

export interface LevelDistribution {
  level: number;
  totalUsers: number;
  lowerLevel: { count: number; percent: number };
  sameLevel: { count: number; percent: number };
  higherLevel: { count: number; percent: number };
}

export interface AccountDeletionState {
  status: 'pending' | 'running';
  /** ms */
  requestedAt: number;
  /** ms — ettől kezdve törölhető véglegesen. */
  scheduledFor: number;
}

export interface DataExportState {
  status: 'processing' | 'ready' | 'failed';
  requestedAt: number;
  readyAt?: number;
  /** ms — eddig élnek a letöltési linkek. */
  expiresAt?: number;
}

export interface DataExportResult {
  jsonUrl: string;
  gpxUrl: string;
  expiresAt: number;
}

export interface EarnedBadge {
  id: string;
  /** Unix ms. */
  earnedAt: number;
}

/** Amit a szerver visszaad egy feldolgozott aktivitásról. */
export type TerritoryHistoryItem =
  | {
      kind: 'claimed'; key: string; at: number; activityId: string; layer: string;
      cells: number; stolenCells: number;
      stolenFrom: { userId: string; cells: number; username: string | null }[];
    }
  | {
      kind: 'lost'; key: string; at: number; activityId: string; layer: string;
      cells: number; actorId: string; actorUsername: string | null;
    };

export interface TerritoryHistoryPage {
  items: TerritoryHistoryItem[];
  nextCursor: string | null;
}

/** CONNECTOR (#118): a szerveroldali `ConnectionStatus` tükre. */
export type ConnectorProviderId = 'polar' | 'garmin' | 'hammerhead' | 'wahoo' | 'strava' | 'coros';

export interface ConnectorConnection {
  provider: ConnectorProviderId;
  available: boolean;
  connected: boolean;
  connectedAt: number | null;
  /** Csak Strava: a GRUNDO-rögzítések automatikus feltöltése (#123). */
  autoExport?: boolean;
}

/** Strava-feltöltés állapota (`server/src/lib/connector/stravaExport.ts`). */
export interface StravaExportState {
  status: 'pending' | 'uploading' | 'done' | 'skipped' | 'failed';
  stravaActivityId: string | null;
  reason: string | null;
}

export interface ActivitySummary {
  distanceM: number;
  durationS: number;
  movingS: number;
  cellCount: number;
  loops: number;
  claimedCells: number;
  areaGainedM2: number;
  gp: number;
}

export interface UploadActivityInput {
  activityId: string;
  type: 'run' | 'walk' | 'ride';
  points: readonly { lat: number; lng: number; t: number; accuracy?: number }[];
  startedAt: number;
  endedAt: number;
  movingMs: number;
  /** Diagnosztika — nem befolyásolja a mentés eredményét, lásd `docs/05-adatmodell.md`. */
  device?: {
    platform: 'ios' | 'android' | 'web';
    native: boolean;
    userAgent: string;
    appVersion: string;
    channel: string;
    revision: string;
  };
  lifecycle?: Array<{ kind: 'foreground' | 'background'; at: number }>;
  /** Rögzítési napló és teljesítmény-összegzés — diagnosztika, lásd `tracking/trace.ts`. */
  trace?: import('@/tracking/trace').TraceEvent[];
  perf?: import('./perfMeter').PerfSummaryRow[];
  /** What the user trimmed off before Save — diagnostics only, see `tracking/trim.ts`. */
  trim?: import('@/tracking/recorder').TrimSummary;
  /**
   * Tervezett útvonalból vagy küldetésből indult-e a rögzítés.
   *
   * A „Forrest" és a „Top Gun" jelvényhez kell (`src/game/badges.ts`). NEM
   * játékadat: területet, GP-t, szintet nem befolyásol, és a szerver sem
   * hisz el belőle semmi mást — csak egy számlálót léptet.
   */
  startedFrom?: 'mission' | 'route';
}

export type ActivityUploadStatusResult =
  | { status: 'missing' }
  | { status: 'processing' }
  | { status: 'failed'; message: string; retryable: boolean }
  | { status: 'done'; summary: ActivitySummary };

/**
 * Egy szerző neve és képe — a feedben, a részleteknél, a hozzászólásoknál.
 *
 * ⚠️ AZ `uid` KELL A „RIVÁLIS" CÍMKÉHEZ. A felület a rivális-halmazt
 * azonosító szerint tartja (`RivalProvider`); névre illeszteni törékenyebb
 * lenne, mert az átnevezés után némán rossz eredményt adna.
 */
export interface ActivityAuthor {
  uid: string;
  username: string;
  photoURL: string | null;
  /**
   * A választott cellaszín KULCSA, vagy `null`, ha nem állított magának.
   *
   * ⚠️ A `null` NEM ugyanaz, mint az alapértelmezett szín: a rivális-sáv
   * ilyenkor a régi lila-magenta párost tartja meg (lásd `rivalBarColors`).
   */
  cellColor?: string | null;
}

export type FeedScope = 'mine' | 'world' | 'local' | 'following' | 'user';

export interface FeedActivity {
  /** A befejezés ideje — a feed ez szerint rendez és dátumoz. */
  endedAt?: number;
  /** Csak a SAJÁT, nem érvényes aktivitásnál jön le (#91): „Ellenőrzés alatt" / „Elutasítva". */
  trustVerdict?: 'pending_review' | 'rejected';
  durationS?: number;
  id: string;
  type: 'run' | 'walk' | 'ride';
  layer: 'foot' | 'bike';
  startedAt: number;
  distanceM: number;
  movingS: number;
  areaGainedM2: number;
  /**
   * A KIJELZETT terület: új + megerősített + elvett (GRUNDO #62). A régebbi
   * backend nem küldi — akkor az `areaGainedM2` áll a helyén.
   */
  areaClaimedM2?: number;
  gp: number;
  /** A nyomvonal közepe — a helyi szűréshez és a térképhez. */
  center: { lat: number; lng: number } | null;
  /**
   * A kódolt nyomvonal a kártya térképéhez — MÁR LEVÁGVA a privát zónával.
   * Üres sztring, ha az egész aktivitás a védőkörön belül zajlott.
   */
  route: string;
  /** Üres a nyomvonal, mert a privát zóna lefedte — nem pedig azért, mert nincs. */
  routeHidden: boolean;
  /** Who may see it. Missing from a backend older than GRUNDO #86. */
  visibility?: Visibility;
  /** A felhasználó által adott név. `null` → automatikus cím (napszak + forma). */
  title: string | null;
  photos: ActivityPhoto[];
  likeCount: number;
  commentCount: number;
  likedByMe: boolean;
  activityCells: string[];
  /**
   * A nagy (compact) hurkok belseje, H3-compactolt indexekkel. A kliens
   * bontja ki res12-re — lasd expandActivityCells().
   */
  activityCellParents?: string[];
  author: ActivityAuthor;
  /** Hány mezővel nőtt a grund ebben a körben (szabad földről + elvéve). */
  cellsGained: number;
  /** Ebből mennyi jött MÁS JÁTÉKOSTÓL. */
  cellsStolen: number;
  /**
   * A kártya alján futó rivális-sáv adata — `null`, ha a kör nem vett el
   * senkitől területet.
   *
   * ⚠️ A SZÁMOK A TELJES, HALMOZOTT MÉRLEGET mutatják a szerző és a kör fő
   * károsultja között, NEM ennek az egy aktivitásnak az eredményét. Ez
   * szándékos (Geri, 2026-08-26): a sáv ugyanaz a rivális-kártya, ami a
   * profilon és a `/profil/rivalisok` fülön van, hogy egy pillantással
   * ugyanazt jelentse mindenhol.
   *
   * Az EGYETLEN aktivitáshoz kötött adat a `cellsThisActivity` — ez kerül a
   * sáv bal felső sarkába, a jobb felső szorzó párjaként.
   */
  rival: ActivityRival | null;
}

export interface ActivityRival extends ActivityAuthor {
  exchangedCells: number;
  gainedCells: number;
  lostCells: number;
  gainedEvents: number;
  lostEvents: number;
  exchangedM2: number;
  gainedM2: number;
  lostM2: number;
  /** Amit EBBEN a körben vett el tőle. */
  cellsThisActivity: number;
  /** A kör többi károsultja — jelvényként a fő kép sarkában. */
  others: ActivityRivalOther[];
}

/**
 * A kör egy további károsultja. A darabszámot a régebbi backend nem küldi
 * (GRUNDO #62 előtt) — ilyenkor a magyarázó lap szám nélkül listázza.
 */
export interface ActivityRivalOther extends ActivityAuthor {
  cellsThisActivity?: number;
}

/** Egy feltöltött kép szerver által ellenőrzött Storage-útvonala. */
/** What a signed-out visitor sees of a public activity (#105, `GET /api/public/activities/:id`). */
export interface PublicActivity {
  id: string;
  type: 'run' | 'walk' | 'ride';
  title: string | null;
  description: string | null;
  startedAt: number;
  endedAt: number;
  durationS: number;
  movingS: number;
  distanceM: number;
  areaM2: number;
  gp: number;
  author: { username: string };
  /** Relative to the API host: the activity's share image (map + band + logo). */
  imagePath: string;
}

export interface ActivityPhoto {
  path: string;
  /** @deprecated Csak a már telepített, régi natív kliensek kompatibilitásához. */
  url?: string;
}

export interface ActivityComment {
  id: string;
  text: string;
  createdAt: number;
  /** A sajátom-e — ettől függ, törölhető-e. */
  mine: boolean;
  /** Soft-delete/moderáció után csak helyőrző jelenik meg, a szál megmarad. */
  hidden: boolean;
  author: ActivityAuthor;
  /** Melyik kommentre válaszol — `null`, ha ez egy önálló hozzászólás. */
  replyToId: string | null;
  /** A megcélzott felhasználó neve, denormalizálva — lásd a szerver oldali megjegyzést. */
  replyToUsername: string | null;
}

/** Egy aktivitás teljes adatlapja — a részletek képernyőhöz. */
export interface ActivityDetail {
  createdAt?: number;
  id: string;
  /** Imported from a connected service (CONNECTOR, K6): which one, and the recording device. */
  source?: { provider: ConnectorProviderId; device: string | null };
  /** A sajátom-e. Ettől függ, mit szabad mutatni és mit lehet szerkeszteni. */
  mine: boolean;
  type: 'run' | 'walk' | 'ride';
  layer: 'foot' | 'bike';
  title: string | null;
  startedAt: number;
  endedAt: number;
  distanceM: number;
  durationS: number;
  movingS: number;
  /** Server-side map-matched SRTM elevation, available for new activities. */
  ascentM?: number;
  descentM?: number;
  areaGainedM2: number;
  /**
   * A KIJELZETT terület: új + megerősített + elvett (GRUNDO #62). A régebbi
   * backend nem küldi — akkor az `areaGainedM2` áll a helyén.
   */
  areaClaimedM2?: number;
  /** A GP teljes bontása: alap, igény, lopás, áttörés, sorozatszorzó. */
  gp: {
    base?: number;
    claim?: number;
    steal?: number;
    breakthrough?: number;
    streakMult?: number;
    softCapReduction?: number;
    total: number;
  };
  cellCount: number;
  loops: number;
  claimedCells: number;
  /**
   * A mezők SORSA — új, megerősített, elvett, áttört.
   *
   * ⚠️ HIÁNYOZHAT. A szerver csak a 2026-08-26 utáni mentéseknél tudja
   * (`claimCounts`); a régebbieknél nem pótolható, ezért inkább nem küld
   * semmit, mint hamis nullákat. A kliens ilyenkor ne rajzoljon bontást.
   */
  claimFates?: {
    free: number;
    reclaimed: number;
    stolen: number;
    breakthrough: number;
  };
  route: string;
  routeHidden: boolean;
  /** Who may see it. Missing from a backend older than GRUNDO #86. */
  visibility?: Visibility;
  description: string | null;
  photos: ActivityPhoto[];
  likeCount: number;
  commentCount: number;
  likedByMe: boolean;
  activityCells: string[];
  /**
   * A nagy (compact) hurkok belseje, H3-compactolt indexekkel. A kliens
   * bontja ki res12-re — lasd expandActivityCells().
   */
  activityCellParents?: string[];
  bounds: { north: number; south: number; east: number; west: number } | null;
  author: ActivityAuthor;
  /** Csak a saját aktivitásnál. Pontszám és diagnosztika soha nem jön le. */
  trustVerdict?: 'trusted' | 'pending_review' | 'rejected';
  /** Csak a saját aktivitásnál: a fellebbezés állapota (#91). */
  appealStatus?: 'open' | 'resolved' | null;
}

export interface FeedResult {
  nextCursor?: string | null;
  activities: FeedActivity[];
  /** A helyi nézet a vizsgált halmaz végéig ért — lehet, hogy van még. */
  truncated?: boolean;
}

export interface FeedQuery {
  cursor?: string;
  scope: FeedScope;
  limit?: number;
  /** Opcionális kezdő- és végidő (Unix ms), a felhasználó helyi naptárából. */
  dateFrom?: number;
  dateTo?: number;
  /** Csak a `local` nézethez. */
  lat?: number;
  lng?: number;
  radiusKm?: number;
  /** Csak a `user` nézethez: kinek az aktivitásait kérjük. */
  userId?: string;
  /** `world`/`local`: a saját és a követett szerzők nélkül — a Felfedezés „új arcai" (#71). */
  excludeFollowed?: boolean;
}

export interface TerritoryResult {
  layer: 'foot' | 'bike';
  cells: { cell: string; defense: number }[];
  cellCount: number;
  areaM2: number;
  blockCount: number;
  truncated?: boolean;
}

export interface TileCell {
  cell: string;
  owner: string;
  defense: number;
}

/** Egy mező tulajdonosának nyilvános kártyája — a térképi koppintáshoz. */
export interface TileOwner {
  uid: string;
  username: string;
  photoURL: string | null;
  level: number;
  rankName: string;
  gpTotal: number;
  /** A megjelenített réteg szerinti terület és mezőszám. */
  areaM2: number;
  cellCount: number;
  layer: 'foot' | 'bike';
}

export interface TilesResult {
  layer: 'foot' | 'bike';
  cells: TileCell[];
  /** A nézetet lefedő res 9 blokkok — ezekből számoljuk a SZABAD cellákat. */
  blocks?: string[];
  owners: Record<string, string>;
  /**
   * uid → választott cellaszín KULCSA (nem hexkód), ha a játékos állított be
   * ilyet. A hiányzó bejegyzés nem hiba: az alapértelmezett színt jelenti.
   *
   * Régebbi backend-verzió egyáltalán nem küldi — ezért opcionális.
   */
  ownerColors?: Record<string, string>;
  /**
   * A háló csak a nézet KÖZEPÉT fedi le — a széleken nem tudjuk, mi van.
   * Ilyenkor a felület ne állítsa, hogy ott szabad a terület.
   */
  partial?: boolean;
}

/** Egy összefüggő területfolt — a térkép fő területrétegének egysége. */
export interface TerritoryBlob {
  id: string;
  owner: string;
  areaM2: number;
  cellCount: number;
  /** GeoJSON gyűrűk: [gyűrű][pont][lng, lat]. Az első a külső, a többi lyuk. */
  rings: [number, number][][];
}

export interface TerritoryBlobsResult {
  layer: 'foot' | 'bike';
  blobs: TerritoryBlob[];
  owners: Record<string, string>;
  ownerColors?: Record<string, string>;
  /** Ekkora terület alatt ezen a nagyításon nem rajzolunk — a felület jelezheti. */
  minAreaM2: number;
  truncated?: boolean;
}

/** A country in my passport (DECISIONS #101). `seen: false` = stamp not played yet. */
export interface PassportCountry {
  iso: string;
  unlockedAt: number;
  firstActivityId: string;
  lastVisitAt: number;
  activityCount: number;
  areaGainedM2: number;
  seen: boolean;
}

/**
 * Hourly server-side snapshot of EVERY territory blob of a layer, for the
 * zoomed-out map (DECISIONS #100). No size filter; may be up to an hour old.
 */
export interface TerritorySnapshotResult {
  layer: 'foot' | 'bike';
  /** Unix ms of the state it shows. */
  generatedAt: number;
  blobs: TerritoryBlob[];
  owners: Record<string, string>;
  ownerColors?: Record<string, string>;
}

export interface LeaderboardEntry {
  uid: string;
  username: string;
  photoURL: string | null;
  /** `alltime` nézetben a jelenlegi terület, egyébként az időszaki bruttó szerzés. */
  areaM2: number;
  cellCount: number;
}

/** `alltime` a jelenlegi állomány, a többi az adott időszak bruttó szerzése. */
export type LeaderboardWindow = 'day' | 'week' | 'month' | 'alltime';

/** Egy toplista-oldal (#117): a `nextCursor` a következő oldal `after`-je, `null`, ha nincs több. */
export interface LeaderboardPage<T> {
  layer: string;
  window: LeaderboardWindow;
  entries: T[];
  /** Régi backend nem küldi — ott nincs lapozás. */
  nextCursor?: string | null;
}

/** Banda-toplista sora: a tagok területének összege a rétegben és időszakban. */
export interface BandaLeaderboardEntry {
  id: string;
  name: string;
  photoURL: string | null;
  memberCount: number;
  areaM2: number;
  /** Publikus banda, vagy a néző tagja — csak ilyenkor nyitható meg. */
  openable: boolean;
}

/**
 * A küldetés négy karaktere — mindegyik MÁS motivációt szolgál ki, nem
 * ugyanannak a fokozatai. docs/02 → Küldetés-ajánló.
 */
export type MissionKind = 'conquest' | 'raid' | 'fortify' | 'explore';
export type MissionPriority = 'balanced' | MissionKind;

/**
 * Útvonal-karakter kapcsoló (döntés: 2026-08-29) — ugyanaz a GraphHopper-
 * tervező futtatja mindkettőt, csak a kanyarbüntetéssel vagy anélkül.
 * Séta mozgásformánál a felület nem kínálja fel.
 */
export type RouteCharacter = 'twisty' | 'straight';
export type RoutePreference = 'fast' | 'protected' | 'quiet';
export type TerrainPreference = 'flat' | 'balanced' | 'hilly';

export interface Mission {
  kind: MissionKind;
  distanceKm: number;
  /** Kódolt vonallánc — a Mapbox statikus térképképe közvetlenül érti. */
  polyline: string;
  /** Optional for compatibility with missions saved before route guidance. */
  maneuvers?: RouteManeuver[];
  /** A megszerezhető ÚJ terület (szabad + elvett). */
  areaM2: number;
  estimatedGp: number;
  cellCount: number;
  counts: { free: number; reclaimed: number; stolen: number; breakthrough: number } | null;
  newBlocks: number;
  /**
   * A célpont neve — CSAK publikus fióknál, és naponta legfeljebb egyszer
   * ugyanaz a személy. A szerver dönti el; `null` esetén a felület
   * „egy helyi játékostól" alakot ír.
   */
  victimName: string | null;
  victimAreaM2: number;
}

export interface MissionResult {
  /** A vállalt időre számolt célhossz — a felület ezt is kiírja. */
  targetKm: number;
  paceSecPerKm: number;
  quota?: { unlimited: true } | { unlimited: false; used: number; limit: number };
  missions: Mission[];
  /** `no_loops`, ha egyetlen jelölt sem zárt kört a környéken. */
  reason?: string;
}

/**
 * Egy jelölt útvonal a GYORS fázisból — terület és GP nélkül.
 *
 * Ennyi elég a kártya kirajzolásához: a vonalláncból megvan a térkép, a
 * hosszból a fejléc. A többi mező a `missionsEvaluate` válaszából érkezik.
 */
export interface PlannedRoute {
  polyline: string;
  maneuvers?: RouteManeuver[];
  distanceKm: number;
  /** Csak diagnosztikához és a kiértékelő kéréshez — a felület nem mutatja. */
  bearing: number;
}

/**
 * A `phase: 'plan'` válasza — a küldetés-ajánló gyors fele.
 *
 * ⚠️ NINCS BENNE TERÜLET, ÉS EZ SZÁNDÉKOS. A „NEM BECSLÉS" szabály szerint
 * nem adunk közelítő számot, amit később felülírnánk: a kártya addig töltő
 * jelzést mutat, amíg a valódi motor ki nem számolja az értéket.
 */
export interface MissionPlanResult {
  targetKm: number;
  paceSecPerKm: number;
  quota?: { unlimited: true } | { unlimited: false; used: number; limit: number };
  routes: PlannedRoute[];
  /** `no_routes` vagy `no_fit` — a `no_loops` csak a kiértékelésből derülhet ki. */
  reason?: string;
}

export interface OtpSendResult {
  sent?: boolean;
  alreadyVerified?: boolean;
  waitSeconds: number;
  /** Csak fejlesztői módban, ha nincs beállítva e-mail-szolgáltató. */
  devCode?: string;
}

export interface EmailChangeResult extends OtpSendResult {
  /** The new address the code was sent to. */
  email: string;
  /** Re-sign-in token: the address change may invalidate the old session. */
  customToken: string;
}

export interface DevActivityListItem {
  id: string;
  userId: string;
  username: string;
  type: 'run' | 'walk' | 'ride';
  layer: 'foot' | 'bike';
  title: string | null;
  startedAt: number;
  distanceM: number;
  durationS: number;
  loops: number;
  claimedCells: number;
  areaGainedM2: number;
  gp: number;
  trustVerdict: 'trusted' | 'pending_review' | 'rejected';
  /**
   * A bizalmi pontszám (0–100), vagy `null`, ha nincs rekord.
   *
   * CSAK az admin útvonalon jön le, szerepkör-kapu mögül. A játékos felületére
   * továbbra sem kerülhet ki — ott a verdikt az egyetlen publikus információ.
   */
  trustScore: number | null;
  /** Amit a pontszám (és a bejelentések plafonja) mondana — `null`, ha nincs rekord. */
  measuredVerdict: string | null;
  /** Hozott-e már moderátor döntést róla (terv 2.1, #65). */
  decided: boolean;
  deleted: boolean;
  hasAudit: boolean;
}

/** Moderátori döntés egy aktivitásról — ma csak rögzítődik, játékhatása nincs. */
export interface DevActivityDecision {
  verdict: 'trusted' | 'rejected';
  note: string;
  by: string;
  /** Unix ms. */
  at: number;
}

/** A bizalmi pontszám részletei — admin-only. */
export interface DevActivityTrust {
  score: number;
  /** részjelenként 0–1 (1 = teljesen rendben) */
  signals: Record<string, number>;
  reasons: string[];
  /** amit a pontszám mondana */
  measuredVerdict: string;
  /** ténylegesen módosított-e birtokviszonyt */
  appliedGameplayDecision: string;
  observeOnly: boolean;
  /** A 7. jel bemenete a legutóbbi újraszámoláskor — `null`, ha még nem érkezett bejelentés. */
  reports: { counted: number; credible: number; credibleIndependent: number } | null;
  adminDecision: DevActivityDecision | null;
}

export interface DevClaimAudit {
  affectedCells: number;
  capturedFree: number;
  stolen: number;
  reinforced: number;
  weakened: number;
  unchangedAtMax: number;
  ownershipChanges: number;
  areaGainedM2: number;
  transitions: Array<{
    kind: 'captured_free' | 'reinforced' | 'stolen' | 'weakened' | 'unchanged_max';
    fromLevel: number;
    toLevel: number;
    count: number;
  }>;
  victims: Array<{
    userId: string;
    username: string;
    stolenCells: number;
    weakenedCells: number;
  }>;
}

export interface DevActivityAudit {
  version: 1;
  appliedToGameplay: boolean;
  claim: DevClaimAudit;
  loops: {
    successful: Array<{
      index: number;
      fromIndex: number;
      toIndex: number;
      wallCells: number;
      interiorCells: number;
      totalCells: number;
      areaM2: number;
      prunedCells: number;
      claim: DevClaimAudit;
    }>;
    rejected: Array<{
      reason: 'interior_too_small' | 'too_large';
      fromIndex: number;
      toIndex: number;
      wallCells: number;
      interiorCells: number;
      prunedCells: number;
      candidateCells?: number;
    }>;
    shortRevisits: number;
    prunedCells: number;
    orphanAbsorbedCells?: number;
  };
  gps: { sourcePoints: number; cellPath: number; droppedPoints: number; largeGaps: number };
}

/** Az aktivitást rögzítő eszköz — admin-only diagnosztika. */
export interface DevActivityDeviceInfo {
  platform: string;
  native: boolean;
  userAgent: string;
  appVersion: string;
  channel: string;
  revision: string;
}

export interface DevActivityLifecycleEvent {
  kind: 'foreground' | 'background';
  at: number;
}

/** Egy rögzítési naplóesemény (`tracking/trace.ts`) — admin-only diagnosztika. */
export interface DevActivityTraceEvent {
  t: number;
  k: string;
  v?: 'f' | 'b';
  d?: Record<string, number | boolean | string>;
}

export interface DevActivityDetail extends DevActivityListItem {
  endedAt: number;
  movingS: number;
  device: DevActivityDeviceInfo | null;
}

/* ── Admin ──────────────────────────────────────────────────────────────── */

/** Egy eszközre tett push-kísérlet eredménye. A token maszkolva érkezik. */
export interface AdminPushAttempt {
  token: string;
  platform: string;
  ok: boolean;
  code: string | null;
  message: string | null;
}

export interface AdminPushTest {
  attempts: AdminPushAttempt[];
  sent: number;
  failed: number;
}

/**
 * Egy szerverre feltöltött főszál-mérés. Ugyanaz, mint a helyi
 * `PerfHistoryEntry`, kiegészítve azzal, mikor érkezett meg.
 */
export interface ServerPerfSnapshot extends PerfHistoryEntry {
  uploadedAt: string | null;
}

/** A `POST /api/jobs/resume-stuck` válasza — lásd `server/src/lib/resumeStuck.ts`. */
export interface ResumeStuckResult {
  results: {
    activityId: string;
    outcome: 'resumed' | 'dry_run' | 'settled' | 'busy' | 'no_track' | 'error';
    detail?: string;
  }[];
  durationMs: number;
}

/** A `POST /api/jobs/banda-rollover` válasza — lásd `server/src/jobs/bandaRollover.ts`. */
export interface BandaRolloverResult {
  bandasProcessed: number;
  errors: number;
  durationMs: number;
}

export interface AdminStatus {
  role: string | null;
  /**
   * Írhat-e a bejelentkezett admin. NEM védelem, csak a felület udvariassága:
   * a tiltást minden végpont maga is kikényszeríti.
   */
  canWrite: boolean;
  configVersion: number;
  tunableCount: number;
  lastRollover: {
    at: string | null;
    usersProcessed: number;
    holdGpAwarded: number;
    errors: number;
  } | null;
}

export interface SocialAdminStatus {
  email: string | null;
  oauthConfigured: Record<'meta' | 'tiktok', boolean>;
  creativeConfigured: Record<'gemini' | 'video' | 'adobe', boolean>;
  connections: SocialAdminConnection[];
}

export type SocialOAuthProvider = 'meta' | 'tiktok';

export interface SocialAdminConnection {
  id: string;
  provider: SocialOAuthProvider;
  channel: SocialChannel;
  accountId: string;
  accountName: string;
  connectedAt: number;
  expiresAt: number | null;
}

export type SocialChannel = 'facebook' | 'instagram' | 'tiktok';
export type SocialPillar = 'brand' | 'feature' | 'community';
export type SocialPostStatus = 'planned' | 'draft' | 'approved' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'discarded';
export type SocialFormat = 'feed_image' | 'carousel' | 'reel' | 'short_video';
export type SocialVisualTheme = 'white' | 'charcoal';

export interface SocialPost {
  id: string;
  channel: SocialChannel;
  scheduledDate: string;
  scheduledTime: string;
  timeZone: 'Europe/Budapest';
  pillar: SocialPillar;
  format: SocialFormat;
  visualTheme: SocialVisualTheme;
  status: SocialPostStatus;
  title: string;
  caption: string;
  overlayText: string;
  visualPrompt: string;
  mediaUrl: string | null;
  landingUrl?: string;
  publicationUrl?: string | null;
  publicationUrlPending?: boolean;
  publicationMode?: 'automatic' | 'manual' | null;
  publishError?: string | null;
  sourcePhotoUrl?: string | null;
  sourcePhotoHasLogo?: boolean;
  mediaType: 'image' | 'video';
  mediaModel: string | null;
  createdAt: number;
  updatedAt: number;
  copyModel: string | null;
}

export interface ContentEngineImportIssue {
  severity: 'error' | 'warning';
  code: string;
  message: string;
  sheet?: string;
  row?: number;
}

export interface ContentEngineImportPreview {
  fileName: string;
  sha256: string;
  valid: boolean;
  processedRows: number;
  sheets: Array<{ name: string; rows: number; referenceRows: number }>;
  counts: { messages: number; variants: number; rotations: number; longSubtitles: number };
  issues: ContentEngineImportIssue[];
}

export interface ContentEngineImportStaging {
  id: string;
  datasetKey: string;
  mappingVersion: number;
  sha256: string;
  status: 'staging' | 'staged' | 'failed';
  processedRows: number;
  warningCount: number;
  createdAt: number;
  completedAt: number | null;
}

export interface ContentEngineDiffRow {
  key: string;
  incomingKey: string | null;
  kind: 'brandRule' | 'message' | 'variant' | 'combination' | 'rotation' | 'aiRule';
  sourceKey: string;
  sourceRowId: string | null;
  sheet: string | null;
  row: number | null;
  currentRevision: number | null;
  currentVersionId: string | null;
  outcome: 'unchanged' | 'created' | 'revised' | 'conflict' | 'deferred';
  missing: boolean;
  fields: Array<{ name: string; baseline: string | null; current: string | null; incoming: string | null; change: 'unchanged' | 'incoming' | 'current' | 'conflict' }>;
}
export interface ContentEngineImportDiff {
  importId: string;
  datasetKey: string;
  diffVersion: number;
  hash: string;
  basis: { activeSnapshotId: string | null; revision: number; editEpoch: number };
  aliases: Record<string, string>;
  warningCount: number;
  counts: Record<ContentEngineDiffRow['outcome'], number>;
  missingCount: number;
  rows: ContentEngineDiffRow[];
}
export interface ContentEngineReviewInput {
  hash: string;
  aliases: Record<string, string>;
  resolutions: Record<string, Record<string, 'current' | 'incoming'>>;
  warningsAcknowledged: boolean;
  missingAcknowledged: boolean;
  note: string;
}
export interface ContentEngineImportApproval {
  id: string;
  importId: string;
  diffHash: string;
  basis: ContentEngineImportDiff['basis'];
  status: 'approved';
  approvedBy: string;
  approvedAt: number;
  rowCount: number;
}

export interface ContentEngineImportActivation {
  snapshotId: string;
  previousSnapshotId: string | null;
  revision: number;
  rowCount: number;
  activatedAt: number;
  activatedBy: string;
}

export interface ContentEngineImportStatus {
  active: null | { snapshotId: string; revision: number; rowCount: number; activatedAt: number; source: ContentEngineSavedImport };
  imports: ContentEngineSavedImport[];
}
export interface ContentEngineSavedImport extends ContentEngineImportStaging {
  fileName: string;
  counts: ContentEngineImportPreview['counts'];
}

export type ContentEngineLibraryScope = 'brand' | 'policy' | 'templates';
export interface ContentEngineLibraryEntry {
  key: string;
  kind: ContentEngineDiffRow['kind'];
  revision: number;
  versionId: string;
  fields: Record<string, string>;
  editableFields: string[];
}
export interface ContentEngineLibrary {
  basis: ContentEngineImportDiff['basis'];
  groups: Record<ContentEngineLibraryScope, { entries: ContentEngineLibraryEntry[]; hash: string; approved: boolean; approvedAt: number | null }>;
  messages: Array<ContentEngineLibraryEntry & { variants: ContentEngineLibraryEntry[]; verificationStatus: 'unverified' }>;
}

export interface ContentEngineKnowledge {
  basis: ContentEngineLibrary['basis'];
  context: KnowledgeContext;
  records: KnowledgeRecord[];
  messages: Array<{ key: string; sourceId: string; title: string; hash: string; reviewed: boolean;
    sourceVersions: Array<{ key: string; versionId: string }>; knowledgeVersions: Array<{ kind: KnowledgeKind; id: string; versionId: string }>;
    eligibility: { eligible: boolean; blocks: KnowledgeIssue[]; warnings: KnowledgeIssue[] } }>;
}

/** Egy nap a `metricsDaily` aggregátumból. */
export interface MetricsDailyPoint {
  day: number;
  dau: number;
  wau: number;
  mau: number;
  signups: number;
  activities: number;
  distanceKm: number;
  claimedCellsNet: number;
  activeStreaks: number;
  computedAt: string | null;
}

export interface AdminMetrics {
  /** A legutóbbi lezárt nap, vagy `null`, ha még egyszer sem futott az aggregálás. */
  latest: MetricsDailyPoint | null;
  /** Legfrissebb elöl. */
  series: MetricsDailyPoint[];
}

export interface UsagePeriodSummary {
  durationMs: number;
  activeUsers: number;
}

export interface AdminUsageOverview {
  generatedAt: string;
  today: number;
  /** A ténylegesen lekérdezett tartomány (napszám, zárt intervallum). */
  fromDay: number;
  toDay: number;
  /** Csak akkor nem `null`, ha a tartomány pontosan egy napra esik — arra a napra jön az órás bontás. */
  hoursDay: number | null;
  periods: Record<'day' | 'week' | 'month' | 'all', UsagePeriodSummary>;
  /** A `[fromDay, toDay]` tartomány összesítve — a periódusválasztó csempéihez (#67). */
  range: UsagePeriodSummary;
  daily: Array<{ day: number; durationMs: number; activeUsers: number }>;
  hours: Array<{ hour: number; durationMs: number; activeUsers: number }>;
  hourUsers: Array<{ uid: string; name: string; durationMs: number; hours: number[] }>;
}

export interface TunableItem {
  path: string;
  kind: 'number' | 'integer' | 'boolean';
  min?: number;
  max?: number;
  group: string;
  label: string;
  help: string;
  unit?: string;
  /** A kódban rögzített alapérték. */
  defaultValue: number | boolean;
  /** A jelenleg érvényes érték (alapérték vagy felülírás). */
  value: number | boolean;
  overridden: boolean;
}

export interface GameplayState {
  version: number;
  updatedAt: string | null;
  updatedBy: string | null;
  note: string | null;
  overrides: Record<string, number | boolean>;
  rejected: Array<{ path: string; value: unknown; reason: string }>;
  groups: Array<{ group: string; items: TunableItem[] }>;
}

export interface GameplayVersion {
  version: number;
  overrides: Record<string, number | boolean>;
  note: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface ChangelogEntry {
  version: string;
  buildNumber: number | null;
  type: 'patch' | 'minor' | 'major';
  releasedAt: string | null;
  changes: string[];
}

export type ModifierKindName = 'gp_multiplier' | 'claim_multiplier' | 'hold_multiplier';
export type ModifierScopeName = 'global' | 'area' | 'segment';
export type ModifierState = 'active' | 'scheduled' | 'expired' | 'cancelled';

/**
 * A nyilvános szabálymagyarázó egy sora.
 *
 * SZŰKEBB, mint az admin `TunableItem`: nincs benne `min`/`max` (ez nem
 * szerkesztő) és a Trust Score-csoport itt eleve nem is jelenik meg — a
 * szerver zárja ki, lásd `src/config/tunables.ts` → `playerVisible`.
 */
export interface PublicTunableItem {
  path: string;
  kind: 'number' | 'integer' | 'boolean';
  unit?: string;
  label: string;
  help: string;
  defaultValue: number | boolean;
  value: number | boolean;
  overridden: boolean;
}

export interface PublicActiveModifier {
  id: string;
  kind: ModifierKindName;
  scope: ModifierScopeName;
  value: number;
  reason: string;
  from: string;
  to: string;
}

export interface RulesState {
  version: number;
  groups: Array<{ group: string; items: PublicTunableItem[] }>;
  activeModifiers: PublicActiveModifier[];
  terrainScoring: {
    mode: 'shadow' | 'active';
    sourceVersion: string;
    modelVersion: number;
    maxBonusPercent: number;
    bands: Array<{
      minSlopePct: number | null;
      maxSlopePct: number | null;
      multiplier: number;
    }>;
  };
}

export interface AdminModifier {
  id: string;
  kind: ModifierKindName;
  scope: ModifierScopeName;
  value: number;
  reason: string;
  source: 'manual' | 'auto';
  from: string | null;
  to: string | null;
  areaCellCount: number;
  area: { lat: number; lng: number; radiusKm: number } | null;
  segment: { inactiveDays?: number } | null;
  createdBy: string | null;
  cancelled: boolean;
  state: ModifierState;
}

export interface CreateModifierInput {
  kind: ModifierKindName;
  scope: ModifierScopeName;
  value: number;
  reason: string;
  /** ISO időpontok. */
  from: string;
  to: string;
  area?: { lat: number; lng: number; radiusKm: number };
  segment?: { inactiveDays: number };
}

/* ═══════════════════════════════════════════════════════════════════
   Nyilvános profil és közösségi gráf
   ═══════════════════════════════════════════════════════════════════ */

/**
 * A fejléc — EZ MINDIG MEGJÖN, privát fióknál és tiltásnál is.
 *
 * A `restricted: true` válaszban csak ennyi van; a felület ebből is fel tudja
 * építeni a fejlécet és a Követés kérése gombot.
 */
export interface PublicProfileHeader {
  uid: string;
  username: string;
  usernameLower: string;
  photoURL: string | null;
  /** A csatlakozás ideje (Unix ms), `null`, ha a régi profilon nincs dátum. */
  memberSince: number | null;
  pro: { active: boolean };
  account: 'public' | 'private';
  /** Privát fiókon és tiltásnál is látszik — a jelvény elismerés, nem tevékenységi adat. */
  badges: EarnedBadge[];
  /**
   * Virtuális (Alfa Teszt) profil — nem követhető, a profilon diszkrét címke
   * jelzi (Geri, 2026-10-14). Régebbi backendnél hiányzik.
   */
  isVirtual?: boolean;
}

/** A teljes profil — csak akkor jön, ha `restricted: false`. */
export interface PublicProfile extends PublicProfileHeader {
  bio: string | null;
  city: string | null;
  countryCode: string | null;
  gpTotal: number;
  territoryM2: { foot: number; bike: number };
  cellCount: { foot: number; bike: number };
  zoneCount: { foot: number; bike: number };
  streak: { current: number; longest: number; weeks: number };
  counters: {
    activities: number;
    followers: number;
    following: number;
    distanceKm: { run: number; walk: number; ride: number };
  };
}

export interface Relationship {
  /** A saját profilom — ilyenkor nincs se követés, se tiltás. */
  self: boolean;
  /** Én követem őt. */
  following: boolean;
  /** Ő követ engem. */
  followedBy: boolean;
  /** Privát fióknál elküldött, még el nem bírált kérés. */
  requested: boolean;
  /** Én tiltottam le őt. (Aki engem tiltott le, annak a profilja 404.) */
  blocked: boolean;
  /**
   * Én némítottam őt (terv 1.4b): a közös feedekből kimarad, a követés
   * marad. A régebbi backend nem küldi — hiányában „nincs némítva".
   */
  muted?: boolean;
}

export type PublicProfileResult =
  | { profile: PublicProfileHeader; relationship: Relationship; restricted: true }
  | { profile: PublicProfile; relationship: Relationship; restricted: false };

/** A bejelentés okai — a `docs/05` `reports.category` értékkészlete. */
/** Tartalom-bejelentés célpontja (#80) — a szerver `lib/contentReports.ts` `parseContentRef`-je. */
export type ContentReportTarget =
  | { targetType: 'comment'; activityId: string; commentId: string }
  | { targetType: 'banda_post'; bandaId: string; postId: string }
  | { targetType: 'banda_comment'; bandaId: string; postId: string; commentId: string }
  | { targetType: 'banda_chat'; bandaId: string; messageId: string }
  | { targetType: 'banda'; bandaId: string }
  | { targetType: 'news_post'; postId: string }
  | { targetType: 'news_comment'; postId: string; commentId: string };

export type ReportCategory =
  | 'gps_spoof'
  | 'vehicle'
  | 'wrong_type'
  | 'offensive'
  | 'privacy'
  | 'other';

export type FollowStatus = 'following' | 'requested' | 'none';

/** Egy sor a követő- vagy követett-listában. */
export interface Connection {
  uid: string;
  username: string;
  photoURL: string | null;
  /**
   * A választott cellaszín KULCSA, vagy `null`/hiányzó, ha nem állított
   * magának. A rivális-sáv ebből színezi a hozzá tartozó oldalt — lásd
   * `ActivityAuthor.cellColor`.
   */
  cellColor?: string | null;
}

/** Egy találat a Felfedezés fülön indított keresésben — a Connection bővítve a Követés gombhoz kellő állapottal. */
export interface DiscoverUser extends Connection {
  account: 'public' | 'private';
  /** `requested` sosem jön a szervertől — a kliens a Követés gomb kattintása után állítja be helyben. */
  followStatus: FollowStatus;
}

/**
 * Egy rivális — akitől területet vettél el, vagy aki tőled.
 *
 * A FŐ SZÁM az `exchangedCells`: hány mező cserélt gazdát köztetek, mindkét
 * irányban összesen. A `gainedCells`/`lostCells` a bontása — a felületen
 * zölddel és pirossal, kisebb betűvel a fő szám után.
 */
export interface Rival extends Connection {
  exchangedCells: number;
  gainedCells: number;
  lostCells: number;
  gainedEvents: number;
  lostEvents: number;
  exchangedM2: number;
  gainedM2: number;
  lostM2: number;
}

export interface RivalList {
  items: Rival[];
  hasMore: boolean;
  /** Hány rivális kerül kiemelten a profilra — a szerver mondja meg. */
  top: number;
}

export interface ConnectionList {
  items: Connection[];
  /** Igaz, ha a listánál többen vannak — a felület ezt kiírja. */
  hasMore: boolean;
  /**
   * A következő oldal kurzora (terv 1.5) — csak a követő/követett listán.
   * Hiányzik a kedvelőknél és a lapozás előtti szervernél; ilyenkor csak a
   * `hasMore` szól.
   */
  nextCursor?: string | null;
}

/**
 * Banda — csoport, ahol a tagok területe és pontja összeadódik.
 *
 * Phase 1 (GRUNDO #29): nincs még hírfolyam, chat fal és beállítás-mező a
 * kliens oldalán sem — csak az, amit a mag-CRUD képernyők használnak.
 */
export type BandaVisibility = 'public' | 'private';
export type BandaRole = 'owner' | 'moderator' | 'member';

export interface BandaSettings {
  whoCanInvite: 'everyone' | 'moderators' | 'owner';
  inviteCodeVisibleTo: 'everyone' | 'moderators' | 'owner';
  postPermission: 'everyone' | 'moderators' | 'owner';
  publicJoinMode: 'instant' | 'approval';
}

export type BandaPermission = BandaSettings['whoCanInvite'];

/** Ugyanaz a szabály, mint a szerveren (`server/src/lib/bandas.ts` → `canInvite`) — csak a gomb megjelenítéséhez, a tényleges kapu a szerveren van. */
export function canInvite(role: BandaRole, whoCanInvite: BandaSettings['whoCanInvite']): boolean {
  if (role === 'owner') return true;
  if (whoCanInvite === 'owner') return false;
  if (whoCanInvite === 'moderators') return role === 'moderator';
  return true;
}

export interface BandaAreaTotals {
  foot: number;
  bike: number;
}

export interface BandaTotals {
  areaM2: BandaAreaTotals;
  areaDayM2: BandaAreaTotals;
  areaWeekM2: BandaAreaTotals;
  areaMonthM2: BandaAreaTotals;
  gpTotal: number;
  gpWeek: number;
  gpMonth: number;
}

export interface Banda {
  id: string;
  name: string;
  description: string | null;
  photoURL: string | null;
  coverURL: string | null;
  city: string | null;
  visibility: BandaVisibility;
  ownerId: string;
  memberCount: number;
  totals: BandaTotals;
  createdAt: number | null;
}

export interface BandaWithRole extends Banda {
  role: BandaRole;
}

export interface BandaMember {
  uid: string;
  username: string;
  photoURL: string | null;
  role: BandaRole;
  stats?: Record<BandaSport, BandaMemberStats>;
}

export type BandaSport = 'run' | 'walk' | 'ride';
export type BandaPeriod = 'day' | 'week' | 'month' | 'alltime';
export interface BandaMemberStats {
  areaDayM2: number;
  areaWeekM2: number;
  areaMonthM2: number;
  areaTotalM2: number;
  gpDay: number;
  gpWeek: number;
  gpMonth: number;
  gpTotal: number;
}

/** Egy hírfolyam-poszt VAGY egy chat fal-üzenet — ugyanaz az alak mindkettőnél. */
export interface BandaPost {
  id: string;
  authorUid: string;
  authorUsername: string;
  authorPhotoURL: string | null;
  text: string;
  format: 'plain' | 'markdown-v1';
  hasImage: boolean;
  likeCount: number;
  commentCount: number;
  likedByMe: boolean;
  replyToId: string | null;
  replyToUsername: string | null;
  updatedAt: number | null;
  createdAt: number | null;
}

export interface BandaComment {
  id: string;
  authorUid: string;
  authorUsername: string;
  authorPhotoURL: string | null;
  text: string;
  hidden: boolean;
  replyToId: string | null;
  replyToUsername: string | null;
  createdAt: number | null;
}

export interface BandaJoinRequest {
  uid: string;
  username: string;
  photoURL: string | null;
  createdAt: number | null;
}

/** Kitiltott felhasználó (`bandas/{id}/bans/{uid}`). */
export interface BandaBan {
  uid: string;
  username: string;
  photoURL: string | null;
  createdAt: number | null;
}

/** Függő appon belüli meghívó, a vezetők listájához. */
export interface BandaPendingInvite {
  uid: string;
  username: string;
  invitedByUsername: string;
  createdAt: number | null;
}

/** Ennyi 200-as taglap után áll meg a kliens (5000 tag). */
const BANDA_MEMBER_PAGES_MAX = 25;

/** Bejelentett banda az admin moderálásához. */
export interface AdminBandaDetail {
  id: string;
  name: string;
  description: string | null;
  city: string | null;
  photoURL: string | null;
  coverURL: string | null;
  visibility: BandaVisibility;
  memberCount: number;
  ownerId: string;
  ownerUsername: string;
}

/** Egy rám váró banda-meghívó — a `GET /api/bandas/invites/mine` egy sora. */
export interface BandaInvite {
  bandaId: string;
  bandaName: string;
  invitedByUsername: string;
  createdAt: number | null;
}

/**
 * Időjárás-állapotok — pontosan annyi, ahány ikonpárunk van.
 *
 * Mindegyikhez tartozik nappali ÉS éjszakai rajz, ezért minden új állapot két
 * ikont jelent. A szolgáltató ötvennél több kódját a szerver képezi le ezekre
 * (`server/src/routes/weather.ts`).
 */
export type WeatherCondition =
  | 'clear'
  | 'partly_cloudy'
  | 'cloudy'
  | 'rain'
  | 'snow'
  | 'storm'
  | 'fog';

export interface WeatherResult {
  tempC: number;
  condition: WeatherCondition;
  /** Éjszaka van-e a MÉRT helyen — nem a böngésző órája szerint. */
  night: boolean;
  description: string;
  /*
    A kibontott widget három további adata. Mindegyik lehet `null`: a
    hiányzó mérést NEM pótoljuk nullával, mert a „0% csapadék" azt jelenti,
    hogy biztosan nem esik — az pedig nem ugyanaz, mint hogy nem tudjuk.
  */
  /** Csapadék esélye a folyó órában, százalék. */
  precipitationChance: number | null;
  /** Relatív páratartalom, százalék. */
  humidity: number | null;
  /** Szélsebesség, km/h. */
  windKph: number | null;
  /**
   * A mért hely IANA időzónája — a widget alatti sor ebből mutatja a hely
   * pontos idejét. Régebbi backendnél hiányozhat, ezért opcionális.
   */
  timezone?: string | null;
  /** A városnév — csak `place: true` kérésre (saját helyzet). */
  place?: string | null;
}

/** A városkereső egy találata (`GET /api/weather/places`). */
export interface WeatherPlaceHit {
  name: string;
  /** Megye / régió és ország, pl. „Hajdú-Bihar, Magyarország". */
  region: string;
  lat: number;
  lon: number;
}

/* ── Bugreport ────────────────────────────────────────────────────────────── */

export type BugReportKind = 'report' | 'screenshot' | 'video' | 'crash';
export type BugReportStatus =
  | 'new'
  | 'triaged'
  | 'in_progress'
  | 'fixed'
  | 'wontfix'
  | 'duplicate';
export type BugReportSeverity = 'low' | 'normal' | 'high';

export interface BugReportBreadcrumb {
  /** Unix ms. */
  t: number;
  level: string;
  msg: string;
}

export interface BugReportInput {
  kind: BugReportKind;
  severity: BugReportSeverity;
  note: string;
  /** Ugyanaz az alak, mint az aktivitás diagnosztikai eszközadata. */
  device: DevActivityDeviceInfo;
  context: Record<string, unknown>;
  state: Record<string, unknown>;
  logs: BugReportBreadcrumb[];
  crash?: Record<string, unknown>;
}

export interface BugReportCreated {
  reportId: string;
  uploadPrefix: string;
}

export interface BugReportMediaInput {
  path: string;
  contentType: string;
  bytes: number;
  durationMs?: number;
}

export interface AdminBugReportListItem {
  id: string;
  kind: BugReportKind;
  status: BugReportStatus;
  severity: BugReportSeverity;
  /** Unix ms, SZERVERIDŐ. */
  createdAt: number;
  uid: string;
  username: string;
  note: string;
  platform: string;
  appVersion: string;
  revision: string;
  mediaCount: number;
}

export interface AdminBugReportMedia extends BugReportMediaInput {
  /** Rövid életű, aláírt olvasó-URL. Tartós download token nincs. */
  url: string;
}

export interface AdminBugReportDetail extends AdminBugReportListItem {
  device: Record<string, unknown>;
  context: Record<string, unknown>;
  state: Record<string, unknown>;
  logs: BugReportBreadcrumb[];
  crash: Record<string, unknown> | null;
  adminNote: string;
  /** Unix ms, 0 ha még nyitott. */
  resolvedAt: number;
  media: AdminBugReportMedia[];
}

export interface AdminBugReportPatch {
  status?: BugReportStatus;
  severity?: BugReportSeverity;
  adminNote?: string;
}

/** Privát üzenet küldésének törzse (#68). */
export interface DirectMessagePayload {
  text?: string;
  imagePath?: string;
  imageWidth?: number;
  imageHeight?: number;
  replyToId?: string;
}

export interface DirectMessageSendResult {
  conversationId: string;
  messageId: string;
  folder: 'main' | 'requests';
}

/** A privát üzenet egy korábbi változata — csak admin (2026-11-04). */
export interface AdminMessageRevision {
  action: 'edit' | 'delete';
  /** A változtatás ELŐTTI szöveg; `null`: csak kép volt. */
  text: string | null;
  hadImage: boolean;
  revisedAt: number;
  expiresAt: number;
}

export interface DirectMessageCanSend {
  conversationId: string;
  allowed: boolean;
  folder: 'main' | 'requests' | null;
  /** Az ÁLTALÁNOS ok, ha nem írhat (pl. „csak kölcsönös követés esetén fogad üzenetet"). */
  reason: string | null;
}

export interface Presence {
  online: boolean;
  lastSeenAt: number | null;
}

export interface AdminTeamInboxItem {
  uid: string;
  username: string;
  needsTeamReply: boolean;
  updatedAt: number;
}

export interface AdminTeamThread {
  uid: string;
  username: string;
  needsTeamReply: boolean;
  /** Meddig olvasta a felhasználó (Unix ms, 0 = még nem). */
  readAt: number;
  messages: {
    id: string;
    fromTeam: boolean;
    text: string | null;
    hasImage: boolean;
    replyPreview: { senderId: string; text: string | null; hasImage: boolean } | null;
    createdAt: number;
  }[];
}

export interface AdminReportListItem {
  id: string;
  targetType: 'activity' | 'user' | 'message' | ContentReportTarget['targetType'];
  targetId: string;
  /** Aktivitásnál az aktivitás gazdája, felhasználó-bejelentésnél maga a bejelentett. */
  targetUsername: string;
  reporterId: string;
  reporterUsername: string;
  category: ReportCategory;
  categoryLabel: string;
  branch: 'technical' | 'content';
  note: string;
  status: 'open' | 'upheld' | 'dismissed';
  resolution: string;
  /** Unix ms, szerveridő. */
  createdAt: number;
  /** Unix ms, 0 ha még nyitott. */
  resolvedAt: number;
  /** #68: DM-bejelentésnél a bejelentés pillanatképe; a régi szerver nem küldi. */
  message?: { text: string | null; sentAt: number; hadImage: boolean; hasEvidence: boolean } | null;
  /** #80: tartalom-bejelentésnél a pillanatkép (bandánál a `title` a név); a régi szerver nem küldi. */
  content?: { title: string | null; text: string | null; createdAt: number; hadImage: boolean; hasEvidence: boolean } | null;
}


/** Admin felhasználókeresés egy találata. */
export interface AdminUserSummary {
  uid: string;
  username: string;
  displayName: string;
  email: string;
  photoURL: string | null;
  level: number;
  gpTotal: number;
  trustLevel: 'new' | 'established' | 'trusted' | 'watched';
  moderationStatus: 'active' | 'shadowbanned' | 'banned' | 'ban_pending';
  /** Unix ms. */
  createdAt: number;
  pro: boolean;
  territoryM2: { foot: number; bike: number };
  /** Alfa Teszt profil — csak az admin válaszában; régi szervernél hiányzik. */
  alfa?: boolean;
  /** A bejelentkezve használt appok (használati jelzésekből); régi szervernél hiányzik. */
  platforms?: ('ios' | 'android' | 'web')[];
  /** A fiókhoz kötött belépési módok (Firebase Auth); régi szervernél hiányzik. */
  signInMethods?: ('google' | 'apple' | 'password')[];
  /** A Google/Apple-fióktól kapott név — nem azonos a felhasználónévvel; régi szervernél hiányzik. */
  providerName?: string | null;
  /** Megerősített-e az e-mail (Auth, Google/Apple-fiók vagy OTP); régi szervernél hiányzik. */
  emailVerified?: boolean;
}

export interface AdminTesterSummary {
  uid: string;
  username: string;
  displayName: string;
}

export interface AdminUserActivity {
  id: string;
  type: string;
  distanceM: number;
  startedAt: number;
  endedAt: number;
  /** Unix ms, null ha nincs törölve. */
  deletedAt: number | null;
  /** `'owner'`: a felhasználó törölte. `'admin_ban'`: tiltás miatt rejtve. `null`: nincs törölve. */
  deletedBy: 'owner' | 'admin_ban' | null;
}

/** Az aktivitás-felülvizsgálat listanézete. */
export type DevActivityView = 'all' | 'pending' | 'decided';

/** Egy tétel a moderációs sorban — lásd `server/src/lib/moderationQueue.ts`. */
export interface ModerationQueueItem {
  kind: 'review' | 'appeal' | 'report';
  id: string;
  userId: string;
  username: string;
  queuedAt: number;
  dueAt: number;
  overdue: boolean;
  /** Admin-only: a bizalmi pontszám és a mentéskori szint (aktivitásnál). */
  score: number | null;
  level: string | null;
  summary: string;
  targetType: string | null;
  targetId: string | null;
}

export interface AdminUserDetail extends AdminUserSummary {
  counters: { activities: number; followers: number; following: number };
  trust: {
    level: AdminUserSummary['trustLevel'];
    cleanActivities: number;
    upheldReports: number;
    /** A `watched` szint vége (Unix ms); `null`, ha sosem volt megfigyelve. */
    watchedUntil: number | null;
  };
  moderation: Record<string, unknown> | null;
  activities: AdminUserActivity[];
}

/** Egy címkeresési találat — lásd `server/src/lib/geocode.ts`. */
export interface PlaceHit {
  label: string;
  lat: number;
  lng: number;
  distanceM: number;
}

/**
 * A „Grout” asszisztens értelmezése — lásd `server/src/lib/routeInterpret.ts`.
 * A `null` mező: a mondatból nem derült ki, a felület a mostani beállítást hagyja.
 */
export interface RouteInterpretation {
  status: 'ok' | 'clarify' | 'not_route';
  question: string | null;
  activity: 'run' | 'walk' | 'bike' | null;
  profile: 'walking' | 'cycling' | null;
  mode: 'loop' | 'direct' | null;
  start: { kind: 'current_location' | 'place'; name: string | null } | null;
  end: { kind: 'current_location' | 'place'; name: string | null } | null;
  stops: string[];
  /** Csak kiírjuk — a kerülő-méretre nem váltjuk át (játékszabály). */
  distanceKm: number | null;
  detour: 'small' | 'medium' | 'large' | null;
  preference: 'fast' | 'protected' | 'quiet' | null;
  terrain: 'flat' | 'balanced' | 'hilly' | null;
  preferCycleways: boolean | null;
  unsupported: string[];
  /** #86: a vague destination the server looked up near the user (older servers: absent). */
  target?: { kind: 'water' | 'park' | 'forest' | 'center' | 'nearby'; name: string | null } | null;
  /** #86: „rádbízom” — the server chose instead of asking. */
  delegate?: boolean;
  /** #86: „parkon át” — the server put a park on the way as the first stop. */
  viaGreen?: boolean;
}

export interface AssistantPlace {
  kind: 'current_location' | 'place';
  name: string | null;
  /** Legfeljebb 3 geokódolt jelölt, távolság szerint; üres, ha nincs találat. */
  candidates: PlaceHit[];
  /** A memóriából jött: a felhasználó korábban már jóváhagyta ezt a pontot (#79). */
  remembered?: boolean;
  /** #86: the server picked it (delegated, or the single nearest shore / town centre). */
  decided?: boolean;
}

export interface RouteInterpretResult {
  ok: true;
  interpretation: RouteInterpretation;
  places: { start: AssistantPlace | null; end: AssistantPlace | null; stops: AssistantPlace[] };
  quotaLeft: number;
  elapsedMs: number;
}

/**
 * A tervező mostani állapota a beszélgetéshez (#79) — lásd
 * `server/src/lib/assistantChat.ts` → `PlanContext`. A címkék a felhasználó saját
 * választásai; a szerver adatként adja a modellnek.
 */
export interface AssistantPlanContext {
  activity: 'run' | 'walk' | 'bike' | null;
  mode: 'loop' | 'direct' | null;
  /** `'current'`: a jelenlegi helyzet. */
  from: string | null;
  to: string | null;
  stops: string[];
  detour: 'small' | 'medium' | 'large' | null;
  preference: 'fast' | 'protected' | 'quiet' | null;
  terrain: 'flat' | 'balanced' | 'hilly' | null;
  preferCycleways: boolean | null;
}

/** Egy beszélgetés-forduló eredménye: a válasz és az értelmezett VÁLTOZÁS. */
export interface AssistantMessageResult extends Omit<RouteInterpretResult, 'ok'> {
  ok: true;
  conversationId: string;
  reply: string;
  /** Amit ebből az üzenetből megjegyzett. */
  remembered: string[];
  /** A mondatban elhangzott beállítások (a változással egyezőek is) — ezekre nem kérdezünk rá. */
  stated?: AssistantFollowUpField[];
  /**
   * #87: the model leads the conversation — tap-to-send replies. Present (even
   * empty) from a #87+ server; the client then skips its fixed question chain.
   */
  quickReplies?: string[];
}

export type AssistantFollowUpField = 'activity' | 'mode' | 'detour' | 'preference' | 'terrain' | 'preferCycleways' | 'viaGreen';

export interface AssistantStoredMessage {
  role: 'user' | 'assistant';
  text: string;
  at: number;
  status?: RouteInterpretation['status'];
}

export interface AssistantConversationSummary {
  id: string;
  title: string;
  updatedAt: number;
  messageCount: number;
}

export interface AssistantMemoryView {
  places: { name: string; label: string; lat: number; lng: number; count: number; lastAt: number }[];
  activities: Partial<Record<'run' | 'walk' | 'bike', number>>;
  prefs: Record<string, number>;
  recent: { text: string; at: number }[];
  facts: { text: string; at: number }[];
}

export type AssistantForgetKind = 'place' | 'fact' | 'recent' | 'habits';

/**
 * A tervezett útvonalon SZEREZHETŐ zsákmány.
 *
 * ⚠️ FELSŐ HATÁR, NEM ÍGÉRET. A tervezett és a ténylegesen megtett út nem
 * ugyanaz (GPS, kitérők), a birtokviszony pedig a tervezés pillanatában igaz —
 * mire odaérsz, más is mozoghatott ugyanott. A felületnek ezt ki kell mondania.
 */
export interface RouteReward {
  closesArea: boolean;
  cells?: number;
  newCells?: number;
  stolenCells?: number;
  /** Hamis, ha a kör túl nagy volt a birtokviszony beolvasásához. */
  ownershipKnown?: boolean;
  areaM2?: number;
  gp?: number;
  topRivals?: { name: string; cells: number; areaM2: number }[];
}

export interface RoutePlanResult {
  ok: true;
  mode: 'loop' | 'direct';
  outbound: [number, number][];
  inbound: [number, number][];
  totalDistanceM: number;
  directDistanceM: number;
  totalDurationS: number;
  /** „Csak oda" módban hamis: nem zár kört, tehát NEM ad területet. */
  closesLoop: boolean;
  /** A vezetett navigáció bemenete — lásd `ghostRoute.ts`. */
  polyline: string;
  maneuvers: RouteManeuver[];
  /**
   * Szakaszonkénti út-osztály: `[kezdőPontIndex, végPontIndex, osztály]`.
   *
   * A térkép ebből tudja, milyen vastagon rajzolja a vonalat — egy lakóutca és
   * egy körút nem egyforma széles. Üres, ha a tervező a Mapbox-tartalékra
   * esett vissza; akkor a vonal egyenletes vastagságú marad.
   */
  roadClasses: [number, number, string][];
  /** Hány tervezés maradt a héten; Pro és admin esetén `null`. */
  quotaLeft: number | null;
  reward: RouteReward | null;
  /** Ha a zsákmány-számítás időkorlátba futott, itt az őszinte indoklás. */
  rewardSkipped: string | null;
  /**
   * A kért, de nem teljesült kritériumok magyar üzenettel (Route Intelligence).
   * Hiányzik a régi szervernél — üresnek kell venni.
   */
  unmet?: RouteUnmetNotice[];
  /** Kritériumonkénti teljesülés (csak a kért kritériumok). Régi szervernél hiányzik. */
  fulfillment?: RouteFulfillment;
  /** A csak részben teljesült kritériumok a mért értékkel („Részben teljesült”, #75). */
  partial?: RouteUnmetNotice[];
  /**
   * Alak-figyelmeztetések (csak körnél): a kör visszafordul, a visszaút az
   * odaúton jön, vagy a két fele nem válik szét. A terv ettől még használható;
   * a felület kimondja, a felhasználó dönt (#75). Régi szervernél hiányzik.
   */
  warnings?: RouteWarning[];
  /**
   * Lazítási ajánlatok: ugyanez a terv egy nem teljesült kritérium nélkül, és
   * csak ha minden MÁSIK kért kritériumot teljesít. Ugyanabban a kérésben jön,
   * a heti keretből nem fogy; a választás a felhasználóé.
   */
  offers?: RouteOffer[];
}

export type RouteCriterion = 'distance' | 'quiet' | 'protected' | 'cycleway' | 'terrain';

export interface RouteCriterionResult {
  status: 'met' | 'partial' | 'unmet' | 'unknown';
  /**
   * A kérést teljesítő részarány (0–1); „dombos” kérésnél emelkedés m/km,
   * „sík” kérésnél a nem meredek rész aránya.
   */
  value: number | null;
  coverage: number;
  ascentM?: number;
  /** „Sík” kérésnél: ennyi méter megy ≥10%-os emelkedőn. */
  steepM?: number;
  /** „Sík” kérésnél: a cél ennyivel van magasabban (negatív: lejjebb). */
  netClimbM?: number;
}

export type RouteFulfillment = Partial<Record<RouteCriterion, RouteCriterionResult>>;

export interface RouteWarning {
  code: 'u_turn' | 'shared_path' | 'not_separated';
  message: string;
}

export interface RouteUnmetNotice {
  criterion: RouteCriterion;
  message: string;
}

/** Egy lazított változat — a fő terv útvonal-mezőivel, csere után ugyanúgy használható. */
export type RouteOffer = Pick<
  RoutePlanResult,
  | 'outbound'
  | 'inbound'
  | 'totalDistanceM'
  | 'directDistanceM'
  | 'totalDurationS'
  | 'closesLoop'
  | 'polyline'
  | 'maneuvers'
  | 'roadClasses'
  | 'unmet'
  | 'partial'
  | 'warnings'
> &
  Partial<Pick<RoutePlanResult, 'reward' | 'rewardSkipped'>> & {
    /** A kritérium, amiről ez az ajánlat lemond. */
    relaxed: RouteCriterion;
    /** A fő tervben ez a kritérium nem teljesült, vagy csak részben (akkor ≥10%-kal jobb az ajánlat). */
    reason?: 'unmet' | 'partial';
    /** A csip felirata, pl. „Csend nélkül”. */
    label: string;
    /** Az ajánlat az EREDETILEG kért beállítások szerint — a csip ebből mutat arányt. */
    askedFulfillment?: RouteFulfillment;
    /** A beállítások, amikkel készült — választáskor a tervező ezeket veszi át. */
    settings: { preference: RoutePreference; terrain: TerrainPreference; preferCycleways: boolean };
  };

export type RoutePlanInput = {
  from: { lat: number; lng: number };
  to: { lat: number; lng: number };
  stops?: { lat: number; lng: number }[];
  profile: 'walking' | 'cycling';
  mode: 'loop' | 'direct';
  detour?: 'small' | 'medium' | 'large';
  preference?: RoutePreference;
  terrain?: TerrainPreference;
  preferCycleways?: boolean;
};

export const api = {
  me: () => request<{ profile: Profile }>('/api/me'),
  syncLocale: () => request<{ locale: 'hu' | 'en' }>('/api/auth/locale', { method: 'PATCH' }),

  levelDistribution: () =>
    request<LevelDistribution>('/api/users/me/level-distribution'),

  /**
   * ÉLŐ KÖZVETÍTÉS NATÍV APPBÓL — adáskulcs a natív mintaküldéshez.
   * Lásd `server/src/routes/live.ts` és `tracking/livePublish.ts`.
   */
  liveOpenSession: (input: { delayMs: number; type: ActivityType }) =>
    request<{ key: string; expiresAt: number }>('/api/live/session', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Az adás vége. `final` nélkül a publikus dokumentum eltűnik. */
  liveCloseSession: (final?: { distanceM: number; movingMs: number }) =>
    request<{ ok: true }>('/api/live/session', {
      method: 'DELETE',
      body: JSON.stringify(final ? { final } : {}),
    }),

  /**
   * Az aktuális időjárás egy koordinátára.
   *
   * A hívás a saját backendünkön megy át, ott van gyorsítótárazva — a
   * szolgáltató kulcsa soha nem kerül kliensre.
   */
  /** `place`: a városnevet is kérjük (fordított geokódolás — csak a saját helyzetnél). */
  weather: (lat: number, lon: number, options: { place?: boolean } = {}) =>
    request<WeatherResult>(`/api/weather?lat=${lat}&lon=${lon}${options.place ? '&place=1' : ''}`),

  weatherPlaces: (query: string) =>
    request<{ items: WeatherPlaceHit[] }>(`/api/weather/places?q=${encodeURIComponent(query)}`),

  /** Egy felhasználó nyilvános profilja, felhasználónév alapján. */
  publicProfile: (username: string) =>
    request<PublicProfileResult>(`/api/users/${encodeURIComponent(username)}`),

  /** Követés — privát fióknál kérés lesz belőle (`requested`). */
  follow: (username: string) =>
    request<{ status: FollowStatus }>(`/api/users/${encodeURIComponent(username)}/follow`, {
      method: 'POST',
    }),

  /** Követés vagy függő kérés visszavonása. */
  unfollow: (username: string) =>
    request<{ status: FollowStatus }>(`/api/users/${encodeURIComponent(username)}/follow`, {
      method: 'DELETE',
    }),

  /**
   * Kik követik, illetve kiket követ — a profil számlálói mögötti lista.
   *
   * A `kind` közvetlenül az útvonal vége, ezért zárt halmaz, nem sztring:
   * elgépelve néma 404 lenne belőle.
   */
  connections: (username: string, kind: 'followers' | 'following', cursor?: string) =>
    request<ConnectionList>(
      `/api/users/${encodeURIComponent(username)}/${kind}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),

  /** Kik kedvelték — CSAK a saját aktivitásra (a szerver 403-mal zár). */
  activityLikers: (activityId: string) =>
    request<ConnectionList>(`/api/activities/${encodeURIComponent(activityId)}/likes`),

  /** Kiket tiltottam le — a Beállítások → Tiltott felhasználók listája. */
  blockedUsers: () => request<{ items: Connection[] }>('/api/users/me/blocked'),
  mutedUsers: () => request<{ items: Connection[] }>('/api/users/me/muted'),

  /** Privát fióknál a várakozó követési kérések (terv 1.3). */
  followRequests: () => request<{ items: Connection[] }>('/api/users/me/follow-requests'),
  acceptFollowRequest: (uid: string) =>
    request<{ status: 'accepted' }>(`/api/users/me/follow-requests/${encodeURIComponent(uid)}/accept`, {
      method: 'POST',
    }),
  declineFollowRequest: (uid: string) =>
    request<{ status: 'declined' }>(`/api/users/me/follow-requests/${encodeURIComponent(uid)}`, {
      method: 'DELETE',
    }),

  /**
   * Nyilvános/privát fiók. Nyilvánosra váltáskor a várakozó kérések
   * automatikusan elfogadódnak — a válasz megmondja, hány.
   */
  setAccountVisibility: (account: 'public' | 'private') =>
    request<{ account: 'public' | 'private'; acceptedRequests: number }>('/api/auth/account', {
      method: 'PATCH',
      body: JSON.stringify({ account }),
    }),

  /** Who sees the user's NEW activities. Existing ones keep their own value. */
  setDefaultVisibility: (defaultVisibility: Visibility) =>
    request<{ defaultVisibility: Visibility }>('/api/auth/default-visibility', {
      method: 'PATCH',
      body: JSON.stringify({ defaultVisibility }),
    }),

  /**
   * Fióktörlés kérése — friss belépés kell, különben a szerver 403
   * `reauth_required` kóddal utasítja el (terv 1.1, `docs/05` → Fióktörlés
   * folyamata).
   */
  requestAccountDeletion: () =>
    request<{ deletion: AccountDeletionState }>('/api/auth/account/deletion', {
      method: 'POST',
    }),

  /** A türelmi időn belüli visszavonás. Utána a kliensnek friss ID tokent kell kérnie. */
  cancelAccountDeletion: () =>
    request<{ deletion: null }>('/api/auth/account/deletion', { method: 'DELETE' }),

  /** A teljes adatcsomag (JSON + GPX) elkészítése — a linkek e-mailben is mennek. */
  exportAccountData: () =>
    request<{ export: DataExportResult }>('/api/auth/account/export', { method: 'POST' }),

  /**
   * A saját rivális-listám, a kicserélt mezők szerint rangsorolva.
   *
   * ⚠️ NINCS `username` PARAMÉTER, és nem is lesz: más rivális-listája nem
   * publikus — megmutatná, kitől szokott veszíteni.
   */
  rivals: () => request<RivalList>('/api/rivals'),

  /** Csak az azonosítók — ebből lesz a név melletti „RIVÁLIS" címke. */
  rivalIds: () => request<{ ids: string[] }>('/api/rivals/ids'),

  bandas: {
    /** Létrehozás — a válasz meghívókódot is ad, ha a banda privát. */
    create: (input: { name: string; description?: string; visibility: BandaVisibility; city?: string }) =>
      request<{ banda: Banda; inviteCode: string | null; role: BandaRole }>('/api/bandas', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    /** A saját bandáim, szerepkörrel. */
    mine: () => request<{ items: BandaWithRole[] }>('/api/bandas/mine'),

    /** Publikus bandák keresése, névprefix szerint. */
    search: (q: string) => request<{ items: Banda[] }>(`/api/bandas/search?q=${encodeURIComponent(q)}`),

    /** Up to ten public bandas ordered by popularity or creation time. */
    /** Banda-toplista (#117): tagok területének összege, 20-asával lapozva. */
    leaderboard: (layer: 'foot' | 'bike', window: LeaderboardWindow, after?: string) =>
      request<LeaderboardPage<BandaLeaderboardEntry>>(
        `/api/bandas/leaderboard?layer=${layer}&window=${window}${after ? `&after=${encodeURIComponent(after)}` : ''}`,
      ),

    discover: (sort: 'popular' | 'new', limit = 10) =>
      request<{ items: Banda[] }>(`/api/bandas/discover?sort=${sort}&limit=${Math.min(10, Math.max(1, limit))}`),

    /** Publikus bandához csatlakozás vagy jóváhagyási kérés. */
    join: (bandaId: string) =>
      request<{ status: 'joined' | 'pending'; role: BandaRole | null }>(`/api/bandas/${encodeURIComponent(bandaId)}/join`, { method: 'POST' }),

    /** Privát bandához meghívókóddal. */
    joinByCode: (code: string) =>
      request<{ role: BandaRole; bandaId: string; name: string }>('/api/bandas/join-by-code', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),

    /** Egy banda részletei — a hívó szerepével, beállításaival és (tagoknál) a meghívókóddal. */
    detail: (bandaId: string) =>
      request<{
        banda: Banda;
        role: BandaRole | null;
        isMember: boolean;
        inviteCode: string | null;
        settings: BandaSettings;
        joinRequestPending: boolean;
        /** Kér-e a hívó értesítést erről a bandáról (a fejléc csengője). */
        notify: boolean;
      }>(`/api/bandas/${encodeURIComponent(bandaId)}`),

    /**
     * A banda csengője. Bandánkénti némítás — a Beállítások → Értesítések
     * globális kapcsolóit nem érinti.
     */
    setNotifications: (bandaId: string, enabled: boolean) =>
      request<{ notify: boolean }>(`/api/bandas/${encodeURIComponent(bandaId)}/notifications`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
      }),

    /**
     * A teljes tag-lista, szerepkörrel. A szerver 200-asával lapoz; a rangsor
     * a TELJES listából számol, ezért itt minden lap betöltődik (legfeljebb
     * `BANDA_MEMBER_PAGES_MAX` lap — e fölött `hasMore: true`).
     */
    members: async (bandaId: string): Promise<{ items: BandaMember[]; hasMore: boolean }> => {
      const items: BandaMember[] = [];
      let after: string | null = null;
      for (let page = 0; page < BANDA_MEMBER_PAGES_MAX; page += 1) {
        const query: string = after ? `?after=${encodeURIComponent(after)}` : '';
        const result: { items: BandaMember[]; nextCursor: string | null } = await request<{ items: BandaMember[]; nextCursor: string | null }>(
          `/api/bandas/${encodeURIComponent(bandaId)}/members${query}`,
        );
        items.push(...result.items);
        after = result.nextCursor;
        if (!after) break;
      }
      return { items, hasMore: after !== null };
    },

    /** Appon belüli meghívás egy másik felhasználónak (GRUNDO #30). */
    invite: (bandaId: string, targetUid: string) =>
      request<{ invited: true }>(`/api/bandas/${encodeURIComponent(bandaId)}/invite`, {
        method: 'POST',
        body: JSON.stringify({ targetUid }),
      }),

    /** A rám váró meghívók. */
    myInvites: () => request<{ items: BandaInvite[] }>('/api/bandas/invites/mine'),

    /** Egy meghívó elfogadása — tag leszek. */
    acceptInvite: (bandaId: string) =>
      request<{ role: BandaRole }>(`/api/bandas/${encodeURIComponent(bandaId)}/invite/accept`, {
        method: 'POST',
      }),

    /** Egy meghívó elutasítása. */
    declineInvite: (bandaId: string) =>
      request<{ declined: true }>(`/api/bandas/${encodeURIComponent(bandaId)}/invite/decline`, {
        method: 'POST',
      }),

    /** A hírfolyam, a legfrissebbel kezdve, tízesével bővíthetően. */
    feed: (bandaId: string, limit = 10) =>
      request<{ items: BandaPost[]; hasMore: boolean }>(`/api/bandas/${encodeURIComponent(bandaId)}/feed?limit=${Math.min(50, Math.max(1, limit))}`),

    /** Új poszt a hírfolyamba — a `settings.postPermission` szerint jogosultsághoz kötve. */
    postToFeed: (bandaId: string, text: string, imagePath?: string) =>
      request<{ id: string }>(`/api/bandas/${encodeURIComponent(bandaId)}/feed`, {
        method: 'POST',
        body: JSON.stringify({ text, format: 'markdown-v1', imagePath }),
      }),

    editFeedPost: (bandaId: string, postId: string, text: string) =>
      request<{ updated: true }>(`/api/bandas/${encodeURIComponent(bandaId)}/feed/${encodeURIComponent(postId)}`, {
        method: 'PATCH', body: JSON.stringify({ text }),
      }),

    deleteFeedPost: (bandaId: string, postId: string) =>
      request<{ deleted: true }>(`/api/bandas/${encodeURIComponent(bandaId)}/feed/${encodeURIComponent(postId)}`, { method: 'DELETE' }),

    toggleFeedLike: (bandaId: string, postId: string) =>
      request<{ liked: boolean; likeCount: number }>(`/api/bandas/${encodeURIComponent(bandaId)}/feed/${encodeURIComponent(postId)}/like`, { method: 'POST' }),

    feedComments: (bandaId: string, postId: string) =>
      request<{ items: BandaComment[] }>(`/api/bandas/${encodeURIComponent(bandaId)}/feed/${encodeURIComponent(postId)}/comments`),

    addFeedComment: (bandaId: string, postId: string, text: string, replyToId?: string) =>
      request<{ id: string }>(`/api/bandas/${encodeURIComponent(bandaId)}/feed/${encodeURIComponent(postId)}/comments`, {
        method: 'POST', body: JSON.stringify({ text, ...(replyToId ? { replyToId } : {}) }),
      }),

    deleteFeedComment: (bandaId: string, postId: string, commentId: string) =>
      request<{ deleted: true }>(`/api/bandas/${encodeURIComponent(bandaId)}/feed/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}`, {
        method: 'DELETE',
      }),

    /** Tagságvédett posztkép; a hívó Blob URL-t készít belőle. */
    feedImage: (bandaId: string, postId: string, signal?: AbortSignal) =>
      requestBlob(
        `/api/bandas/${encodeURIComponent(bandaId)}/feed/${encodeURIComponent(postId)}/image`,
        signal,
      ),

    /**
     * Banda chat (#71, docs/05 → „Banda chat"). Az olvasás élőben, közvetlenül
     * a Firestore-ból megy (`useBandaChat`); ezek csak az írások.
     */
    chat: {
      send: (bandaId: string, text: string, replyToId?: string) =>
        request<{ id: string }>(`/api/bandas/${encodeURIComponent(bandaId)}/chat`, {
          method: 'POST',
          body: JSON.stringify({ text, ...(replyToId ? { replyToId } : {}) }),
        }),
      toggleLike: (bandaId: string, messageId: string) =>
        request<{ liked: boolean; likeCount: number }>(
          `/api/bandas/${encodeURIComponent(bandaId)}/chat/${encodeURIComponent(messageId)}/like`,
          { method: 'POST' },
        ),
      remove: (bandaId: string, messageId: string) =>
        request<{ deleted: true }>(
          `/api/bandas/${encodeURIComponent(bandaId)}/chat/${encodeURIComponent(messageId)}`,
          { method: 'DELETE' },
        ),
      markRead: (bandaId: string) =>
        request<{ read: true }>(`/api/bandas/${encodeURIComponent(bandaId)}/chat/read`, { method: 'POST' }),
      mute: (bandaId: string, memberUid: string, duration: '1h' | '24h' | 'until_lifted') =>
        request<{ muted: true; until: number | null }>(
          `/api/bandas/${encodeURIComponent(bandaId)}/chat/mutes/${encodeURIComponent(memberUid)}`,
          { method: 'PUT', body: JSON.stringify({ duration }) },
        ),
      unmute: (bandaId: string, memberUid: string) =>
        request<{ muted: false }>(
          `/api/bandas/${encodeURIComponent(bandaId)}/chat/mutes/${encodeURIComponent(memberUid)}`,
          { method: 'DELETE' },
        ),
    },

    /** Alapítói jogosultság-beállítások mentése. */
    updateSettings: (bandaId: string, settings: BandaSettings & { visibility?: BandaVisibility }) =>
      request<{ settings: BandaSettings; visibility: BandaVisibility }>(`/api/bandas/${encodeURIComponent(bandaId)}/settings`, {
        method: 'PATCH',
        body: JSON.stringify(settings),
      }),

    /** Banda nevének, leírásának és városának módosítása — csak az alapítónak. Üres szöveg = törlés. */
    updateProfile: (bandaId: string, input: { name?: string; description?: string | null; city?: string | null }) =>
      request<{ name: string; description: string | null; city: string | null }>(`/api/bandas/${encodeURIComponent(bandaId)}/profile`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),

    /** Új meghívókód — a régi azonnal érvénytelen. Csak az alapítónak. */
    rotateInviteCode: (bandaId: string) =>
      request<{ inviteCode: string; previous: string | null }>(`/api/bandas/${encodeURIComponent(bandaId)}/invite-code`, {
        method: 'POST',
      }),

    /** Feloszlatás — minden tagság és tartalom törlődik. Csak az alapítónak. */
    dissolve: (bandaId: string) =>
      request<{ dissolved: true }>(`/api/bandas/${encodeURIComponent(bandaId)}`, { method: 'DELETE' }),

    /** Kitiltott felhasználók — alapítónak és moderátornak. */
    bans: (bandaId: string) =>
      request<{ items: BandaBan[] }>(`/api/bandas/${encodeURIComponent(bandaId)}/bans`),

    unban: (bandaId: string, memberId: string) =>
      request<{ unbanned: true }>(
        `/api/bandas/${encodeURIComponent(bandaId)}/bans/${encodeURIComponent(memberId)}`,
        { method: 'DELETE' },
      ),

    /** Függő appon belüli meghívók — alapítónak és moderátornak. */
    pendingInvites: (bandaId: string) =>
      request<{ items: BandaPendingInvite[] }>(`/api/bandas/${encodeURIComponent(bandaId)}/invites`),

    revokeInvite: (bandaId: string, memberId: string) =>
      request<{ revoked: true }>(
        `/api/bandas/${encodeURIComponent(bandaId)}/invites/${encodeURIComponent(memberId)}`,
        { method: 'DELETE' },
      ),

    /** Banda profil- és borítóképének mentése — csak az alapítónak. */
    updateBranding: (bandaId: string, input: { photoURL?: string | null; coverURL?: string | null }) =>
      request<{ photoURL: string | null; coverURL: string | null }>(`/api/bandas/${encodeURIComponent(bandaId)}/branding`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),

    /** Tag előléptetése moderátorrá vagy vissza taggá. */
    updateMemberRole: (bandaId: string, memberId: string, role: 'moderator' | 'member') =>
      request<{ role: 'moderator' | 'member' }>(
        `/api/bandas/${encodeURIComponent(bandaId)}/members/${encodeURIComponent(memberId)}/role`,
        { method: 'PATCH', body: JSON.stringify({ role }) },
      ),

    /** Alapító/moderátor előtt álló publikus csatlakozási kérelmek. */
    joinRequests: (bandaId: string) =>
      request<{ items: BandaJoinRequest[] }>(`/api/bandas/${encodeURIComponent(bandaId)}/join-requests`),

    reviewJoinRequest: (bandaId: string, memberId: string, decision: 'approve' | 'reject') =>
      request<{ status: 'approved' | 'rejected' }>(
        `/api/bandas/${encodeURIComponent(bandaId)}/join-requests/${encodeURIComponent(memberId)}`,
        { method: 'POST', body: JSON.stringify({ decision }) },
      ),

    /** Tag eltávolítása (`ban` = kitiltás is) — a szerver a szerephierarchiát is ellenőrzi. */
    removeMember: (bandaId: string, memberId: string, ban = false) =>
      request<{ removed: true; banned: boolean; hiddenContent: { bandaPosts: number; bandaChatMessages?: number; bandaComments: number } }>(
        `/api/bandas/${encodeURIComponent(bandaId)}/members/${encodeURIComponent(memberId)}`,
        { method: 'DELETE', body: JSON.stringify({ ban }) },
      ),

    /** Tulajdonjog átadása egy meglévő tagnak; a régi alapító moderátor marad. */
    transferOwnership: (bandaId: string, targetUid: string) =>
      request<{ ownerId: string; previousOwnerRole: 'moderator' }>(
        `/api/bandas/${encodeURIComponent(bandaId)}/transfer-ownership`,
        { method: 'POST', body: JSON.stringify({ targetUid }) },
      ),

    /** Saját tagság megszüntetése; az alapítónak előbb át kell adnia a rangot. */
    leave: (bandaId: string, deleteContent: boolean) =>
      request<{ left: true }>(`/api/bandas/${encodeURIComponent(bandaId)}/leave`, {
        method: 'POST', body: JSON.stringify({ deleteContent }),
      }),
  },

  /**
   * Kit érdemes követni — az onboarding „Első követések" lépése
   * (`docs/02` → Onboarding 7., `FirstFollowsScreen`).
   */
  followSuggestions: () =>
    request<{ items: DiscoverUser[] }>('/api/users/suggestions'),

  /** Felhasználónév-keresés (prefix-illeszkedés) — a fejléc Keresés gombja. */
  searchUsers: (q: string) =>
    request<{ items: Connection[] }>(`/api/users/search?q=${encodeURIComponent(q)}`),

  /**
   * Ugyanaz a végpont, mint `searchUsers`, de a Felfedezés fülön (docs/02 →
   * Közösség → Felfedezés) kell hozzá a fiók-láthatóság és a követési
   * állapot is, hogy a Követés gomb rögtön helyes felirattal induljon.
   */
  discoverSearch: (q: string) =>
    request<{ items: DiscoverUser[] }>(`/api/users/search?q=${encodeURIComponent(q)}`),

  /** Tiltás — a követés mindkét irányban megszűnik. */
  blockUser: (username: string) =>
    request<{ status: 'blocked' }>(`/api/users/${encodeURIComponent(username)}/block`, {
      method: 'POST',
    }),

  /** A tiltás feloldása. A követést NEM állítja vissza. */
  unblockUser: (username: string) =>
    request<{ status: 'none' }>(`/api/users/${encodeURIComponent(username)}/block`, {
      method: 'DELETE',
    }),

  /** Némítás — a közös feedekből kimarad, a követés marad (terv 1.4b). */
  muteUser: (username: string) =>
    request<{ status: 'muted' }>(`/api/users/${encodeURIComponent(username)}/mute`, {
      method: 'POST',
    }),

  unmuteUser: (username: string) =>
    request<{ status: 'none' }>(`/api/users/${encodeURIComponent(username)}/mute`, {
      method: 'DELETE',
    }),

  /** Bejelentés — egy felhasználóról egyszerre egy nyitott bejelentés lehet. */
  reportUser: (username: string, category: ReportCategory, note: string) =>
    request<{ ok: true }>(`/api/users/${encodeURIComponent(username)}/report`, {
      method: 'POST',
      body: JSON.stringify({ category, note }),
    }),

  /**
   * Tartalom bejelentése (#80): hozzászólás, banda-poszt és -komment, banda-chat
   * üzenet, banda. Csak tartalmi ok (sértő, adatvédelmi, egyéb).
   */
  reportContent: (target: ContentReportTarget, category: ReportCategory, note: string) =>
    request<{ ok: true }>('/api/reports', {
      method: 'POST',
      body: JSON.stringify({ ...target, category, note }),
    }),

  /** Fellebbezés az elutasított saját aktivitásra — aktivitásonként egy (#91). */
  appealActivity: (activityId: string, message: string) =>
    request<{ ok: true; appealStatus: 'open' }>(`/api/activities/${encodeURIComponent(activityId)}/appeal`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),

  /** Aktivitás bejelentése — egy aktivitásról egyszerre egy nyitott bejelentés lehet. */
  reportActivity: (activityId: string, category: ReportCategory, note: string) =>
    request<{ ok: true }>(`/api/activities/${encodeURIComponent(activityId)}/report`, {
      method: 'POST',
      body: JSON.stringify({ category, note }),
    }),

  /**
   * Privát üzenetek (#68, docs/05 → „Privát üzenetek"). Az ÍRÁS mind itt megy;
   * a postafiókot és a szálat a kliens közvetlenül olvassa (`useInbox`,
   * `useConversation`).
   */
  messages: {
    canSend: (uid: string) =>
      request<DirectMessageCanSend>(`/api/messages/can-send/${encodeURIComponent(uid)}`),
    sendTo: (uid: string, payload: DirectMessagePayload) =>
      request<DirectMessageSendResult>(`/api/messages/to/${encodeURIComponent(uid)}`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    send: (conversationId: string, payload: DirectMessagePayload) =>
      request<DirectMessageSendResult>(`/api/messages/${encodeURIComponent(conversationId)}/messages`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    markRead: (conversationId: string) =>
      request<{ ok: true }>(`/api/messages/${encodeURIComponent(conversationId)}/read`, { method: 'POST' }),
    /** Szív ki/be egy privát üzeneten (#71, a banda chat mintájára). */
    toggleLike: (conversationId: string, messageId: string) =>
      request<{ liked: boolean; likeCount: number }>(
        `/api/messages/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/like`,
        { method: 'POST' },
      ),
    /** Like (hüvelykujj) ki/be — a szívtől független második reakció. */
    toggleThumbsUp: (conversationId: string, messageId: string) =>
      request<{ thumbsUp: boolean; thumbsCount: number }>(
        `/api/messages/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/thumbs-up`,
        { method: 'POST' },
      ),
    /** Saját szöveges üzenet módosítása (a küldés utáni időablakon belül). */
    edit: (conversationId: string, messageId: string, text: string) =>
      request<{ text: string; editedAt: number | null }>(
        `/api/messages/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}`,
        { method: 'PATCH', body: JSON.stringify({ text }) },
      ),
    /** Saját üzenet törlése (soft delete: a helyén helyőrző marad). */
    remove: (conversationId: string, messageId: string) =>
      request<{ deleted: true }>(
        `/api/messages/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}`,
        { method: 'DELETE' },
      ),
    accept: (conversationId: string) =>
      request<{ folder: 'main' }>(`/api/messages/${encodeURIComponent(conversationId)}/accept`, { method: 'POST' }),
    declineRequest: (conversationId: string) =>
      request<{ folder: 'declined' }>(`/api/messages/${encodeURIComponent(conversationId)}/request`, {
        method: 'DELETE',
      }),
    setMuted: (conversationId: string, muted: boolean) =>
      request<{ muted: boolean }>(`/api/messages/${encodeURIComponent(conversationId)}/mute`, {
        method: muted ? 'POST' : 'DELETE',
      }),
    /** Résztvevő-védett kép; a hívó Blob URL-t készít belőle. Lejárt képre 410. */
    image: (conversationId: string, messageId: string, signal?: AbortSignal) =>
      requestBlob(
        `/api/messages/${encodeURIComponent(conversationId)}/images/${encodeURIComponent(messageId)}`,
        signal,
      ),
    report: (conversationId: string, messageId: string, category: ReportCategory, note: string) =>
      request<{ ok: true }>(
        `/api/messages/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/report`,
        { method: 'POST', body: JSON.stringify({ category, note }) },
      ),
    presence: (uids: readonly string[]) =>
      request<{ presence: Record<string, Presence> }>(
        `/api/messages/presence?uids=${uids.map(encodeURIComponent).join(',')}`,
      ),
  },

  /**
   * Belépés felhasználónévvel. A szerver ellenőrzi a jelszót, és egy
   * custom tokent ad vissza, amivel a Firebase SDK bejelentkeztet.
   * E-mail-címmel belépéskor ez a kerülő nem kell — ott a kliens közvetlenül
   * a Firebase-t hívja, és a jelszó felénk el sem indul.
   */
  login: (username: string, password: string) =>
    request<{ customToken: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  /**
   * Google-fiókos ez az azonosító?
   *
   * CSAK SIKERTELEN BELÉPÉS UTÁN hívjuk. Egy Google-lel regisztrált fiókhoz
   * nem tartozik jelszó, tehát a felhasználó hiába próbálkozik újra — meg kell
   * mondani neki, hogy a Google-gombot keresse.
   *
   * Hibát SOSEM dob: ha a kérdés nem megválaszolható (hálózat, végpont), az
   * eredeti belépési hibaüzenet marad. Egy segítő üzenet hiánya nem indok
   * arra, hogy a valódi hibát elrejtsük.
   */
  signInMethod: (identifier: string) =>
    request<{ googleOnly: boolean; appleOnly: boolean }>('/api/auth/method', {
      method: 'POST',
      body: JSON.stringify({ identifier }),
    }).catch(() => ({ googleOnly: false, appleOnly: false })),

  /**
   * Password reset, step 1: mails a 6-digit code in the app language. Public
   * (no sign-in), and the answer is the same for every address by design.
   */
  passwordResetSend: (email: string) =>
    request<{ sent: boolean; waitSeconds: number; devCode?: string }>('/api/auth/password-reset/send', {
      method: 'POST',
      body: JSON.stringify({ email, locale: getLocale() }),
    }),

  /** Password reset, step 2: code + new password. Public. */
  passwordResetConfirm: (email: string, code: string, password: string) =>
    request<{ reset: boolean }>('/api/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ email, code, password }),
    }),

  /** `dob`: `YYYY-MM-DD` — a szerver ez alapján kényszeríti ki a korhatárt (16 év, `docs/06`). */
  register: (username: string, dob: string) =>
    request<{ profile: Profile }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, dob }),
    }),

  /**
   * Egy befejezett aktivitás feltöltése.
   *
   * A szerver MINDENT újraszámol a nyers nyomvonalból — a kliens által
   * mutatott táv és terület csak előnézet. Eltérés esetén a szerveré az
   * igazság; a válaszban ez jön vissza.
   *
   * A törzs gzip-tömörítve megy — lásd `compressedJsonBody()` fejlécét.
   */
  uploadActivity: async (input: UploadActivityInput) => {
    const { body, headers } = await compressedJsonBody(input);
    return request<
      | { activityId: string; summary: ActivitySummary; duplicate?: boolean }
      | { activityId: string; processing: true }
    >(
      '/api/activities?async=1',
      { method: 'POST', body, headers },
    );
  },

  /**
   * „Nézd meg most a jelvényeimet."
   *
   * Olyan események után kell, amiket a szerver nem lát: a push-token
   * mentése például közvetlenül Firestore-ba megy. Sose dob — egy
   * elmaradt jelvény nem indokolja, hogy a hívó ága eltörjön.
   */
  /** A saját jelvény-haladás létránként — a Jelvények fül „23 / 50" kijelzéséhez. */
  badgeProgress: () =>
    request<{ progress: Partial<Record<import('@/game/badges').BadgeLadder, number>> }>('/api/auth/badges/progress'),

  refreshBadges: () =>
    request<{ queued: boolean }>('/api/auth/badges/refresh', { method: 'POST' }).catch(
      () => ({ queued: false }),
    ),

  /**
   * A készülék push-tokenje a bejelentkezett fiókhoz (#80). A szerver az előző
   * fiók alól elveszi — egy készülék egyszerre egy fiók értesítéseit kapja.
   */
  registerPushToken: (token: string, platform: 'web' | 'ios' | 'android') =>
    request<{ ok: true }>('/api/auth/push-token', {
      method: 'POST',
      body: JSON.stringify({ token, platform, locale: getLocale(), units: getUnitSystem() }),
    }),

  /** Leiratkozás és kijelentkezés: a token levétele a fiókról (#80). */
  releasePushToken: (token: string) =>
    request<{ ok: true }>('/api/auth/push-token/release', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  /** Hosszú mentés állapota megszakadt klienskapcsolat vagy újranyitás után. */
  activityUploadStatus: (id: string) =>
    request<ActivityUploadStatusResult>(
      `/api/activities/${encodeURIComponent(id)}/upload-status`,
    ),

  activityWeek: (from: number, to: number) => request<{ activities: Pick<FeedActivity, 'startedAt' | 'distanceM' | 'movingS' | 'gp'>[] }>(
    `/api/activities/week?from=${from}&to=${to}`,
  ),

  /** A Statisztika fül FREE összesítése típusonként. */
  activityStats: (type: 'run' | 'walk' | 'ride') =>
    request<PersonalStatsResult>(`/api/activities/stats?type=${type}`),

  /** Territory timeline: claims from own activities and losses to other players. */
  territoryHistory: (cursor?: string) =>
    request<TerritoryHistoryPage>(
      `/api/activities/territory-history${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),

  /** A feed — nézet szerint szűrve. */
  activities: (query: FeedQuery) => {
    const params = new URLSearchParams({
      scope: query.scope,
      limit: String(query.limit ?? 10),
    });
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.dateFrom !== undefined) params.set('dateFrom', String(query.dateFrom));
    if (query.dateTo !== undefined) params.set('dateTo', String(query.dateTo));
    if (query.scope === 'local') {
      params.set('lat', String(query.lat ?? 0));
      params.set('lng', String(query.lng ?? 0));
      params.set('radiusKm', String(query.radiusKm ?? 10));
    }
    if (query.scope === 'user') params.set('userId', query.userId ?? '');
    if (query.excludeFollowed) params.set('exclude', 'followed');
    return request<FeedResult>(`/api/activities?${params.toString()}`);
  },

  /** Egy aktivitás adatlapja. */
  activity: (id: string) => request<{ activity: ActivityDetail }>(`/api/activities/${id}`),

  /** Láthatóságvédett aktivitásfotó bináris tartalma. */
  activityPhoto: (id: string, path: string, signal?: AbortSignal) => {
    const fileName = path.split('/').at(-1) ?? '';
    return requestBlob(
      `/api/activities/${encodeURIComponent(id)}/photos/${encodeURIComponent(fileName)}`,
      signal,
    );
  },

  /**
   * A TELJES, levágatlan nyomvonal — csak a saját aktivitásodhoz.
   *
   * A részletek képernyőn ebből rajzoljuk a térképet és számoljuk a
   * részidőket. Másnál a levágott `route` az egyetlen elérhető változat.
   */
  activityTrack: (id: string) =>
    request<{ points: { lat: number; lng: number; t: number; elevation?: number }[] }>(
      `/api/activities/${id}/track`,
    ),

  /**
   * CONNECTOR C1 (#118): a saját aktivitás GPX- vagy TCX-fájlként, a TELJES
   * nyomvonallal — Garmin Connect, Strava (web), Komoot, fejegység. A szerver
   * a `connector` funkciókapun át adja (ma mindenkinek ingyenes).
   */
  activityExport: (id: string, format: 'gpx' | 'tcx') =>
    requestBlob(`/api/activities/${encodeURIComponent(id)}/export?format=${format}`, undefined, undefined, 'file'),

  /** CONNECTOR C2: ugyanez adatként, az Apple Health / Health Connect íráshoz (`healthStore.ts`). */
  activityHealthExport: (id: string) =>
    request<{
      type: ActivityType;
      startedAt: number;
      endedAt: number;
      distanceM: number;
      points: { lat: number; lng: number; t: number; accuracy?: number; elevation?: number }[];
    }>(`/api/activities/${encodeURIComponent(id)}/export?format=json`),

  /** A tervezett kör GPX-pályaként a fejegységre vagy az órára (#118). Rögzíteni továbbra is a GRUNDO-val kell. */
  routeExport: (input: { polyline: string; type: ActivityType; name: string }) =>
    requestBlob('/api/routes/export', undefined, input, 'file'),

  /** CONNECTOR (#118, #119): a gyártói fiókok állapota — `available`: a szerveren be van kötve. */
  connectorList: () =>
    request<{ connections: ConnectorConnection[] }>('/api/connector'),

  /** Az OAuth-cím, amit a kliens megnyit (natívon a rendszerböngészőben). */
  connectorAuthorize: (provider: ConnectorProviderId, returnTo: 'app' | 'web') =>
    request<{ url: string }>(`/api/connector/${provider}/authorize`, {
      method: 'POST',
      body: JSON.stringify({ returnTo }),
    }),

  /** Strava (#123): egy GRUNDO-rögzítés feltöltése most; ami a kérésben nem végez, azt az ütemezett feladat befejezi. */
  connectorStravaExport: (activityId: string) =>
    request<StravaExportState>(`/api/connector/strava/export/${encodeURIComponent(activityId)}`, { method: 'POST' }),

  /** Strava: az automatikus feltöltés kapcsolója. */
  connectorStravaSettings: (autoExport: boolean) =>
    request<{ ok: true }>('/api/connector/strava', { method: 'PATCH', body: JSON.stringify({ autoExport }) }),

  /** Leválasztás — a szolgáltatónál is visszavonja a hozzáférést; a behozott aktivitások maradnak. */
  connectorDisconnect: (provider: ConnectorProviderId) =>
    request<{ ok: true }>(`/api/connector/${provider}`, { method: 'DELETE' }),

  /**
   * Az aktivitás leíró mezőinek szerkesztése.
   *
   * Csak cím, leírás, fotók és láthatóság. A metrikák, a nyomvonal és a pont
   * szerveroldali számítás eredménye, azokat a kliens nem írhatja felül.
   */
  updateActivity: (
    id: string,
    patch: { title?: string; description?: string; photos?: ActivityPhoto[]; visibility?: Visibility },
  ) => request<{ ok: true }>(`/api/activities/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  }),

  /**
   * Share link for the user's OWN activity (#100): the server renders the
   * preview image from the chosen picture and returns the `/s/<code>` URL.
   * The same choice returns the same link. Non-public activity:
   * `409 share_not_public`; private account: `409 share_private_account`.
   */
  createShare: (body: {
    activityId: string;
    image: { kind: 'map' } | { kind: 'photo'; path: string };
    title: string;
    description: string;
  }) => request<{ code: string; url: string }>('/api/shares', {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  /**
   * The share image as a FILE, without a public link (#104): also for a
   * non-public activity, owner only. Nothing is stored on the server.
   */
  shareImage: (
    body: {
      activityId: string;
      image: { kind: 'map' } | { kind: 'photo'; path: string };
      /** `story`: 1080 × 1920 for an Instagram Story (#108); default the 1200 × 630 card. */
      format?: 'card' | 'story';
    },
    signal?: AbortSignal,
  ) => requestBlob('/api/shares/image', signal, body),

  /** A public activity for a signed-out visitor (#105); 404 unless visible to everyone. */
  publicActivity: (id: string) =>
    request<{ activity: PublicActivity }>(`/api/public/activities/${encodeURIComponent(id)}`),

  /** The activity behind a share code — the app link resolves `/s/<code>` with it (#105). */
  publicShareTarget: (code: string) =>
    request<{ activityId: string }>(`/api/public/shares/${encodeURIComponent(code)}`),

  /** Az aktivitás azonnal eltűnik, a szerver 30 napig visszaállíthatóan őrzi. */
  deleteActivity: (id: string) =>
    request<{ ok: true; purgeAt: number }>(`/api/activities/${id}`, { method: 'DELETE' }),

  updateActivityPrivacy: (privacy: {
    hideStart: boolean;
    startRadiusM: 50 | 100 | 200;
    hideEnd: boolean;
    endRadiusM: 50 | 100 | 200;
  }) => request<{ privacy: Profile['privacy']; rebuiltActivities: number }>('/api/auth/privacy', {
    method: 'PATCH',
    body: JSON.stringify(privacy),
  }),

  /** Display name and bio; an empty display name falls back to the username. */
  updateProfileText: (patch: { displayName?: string; bio?: string }) =>
    request<{ displayName?: string; bio?: string }>('/api/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  updateProfilePhoto: (photoURL: string | null) =>
    request<{ photoURL: string | null }>('/api/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify({ photoURL }),
    }),

  /** Kedvelés be- vagy kikapcsolása. A válasz a friss számláló. */
  setLike: (id: string, liked: boolean) =>
    request<{ likeCount: number; likedByMe: boolean }>(`/api/activities/${id}/like`, {
      method: liked ? 'POST' : 'DELETE',
    }),

  comments: (id: string) =>
    request<{ comments: ActivityComment[] }>(`/api/activities/${id}/comments`),

  addComment: (id: string, text: string, replyToId?: string) =>
    request<{ id: string; text: string; createdAt: number; replyToId: string | null; replyToUsername: string | null }>(
      `/api/activities/${id}/comments`,
      {
        method: 'POST',
        body: JSON.stringify({ text, ...(replyToId ? { replyToId } : {}) }),
      },
    ),

  deleteComment: (id: string, commentId: string) =>
    request<{ ok: true }>(`/api/activities/${id}/comments/${commentId}`, { method: 'DELETE' }),

  /** A saját területem cellái, érvényes védelmi szinttel. */
  territory: (layer: 'foot' | 'bike' = 'foot') =>
    request<TerritoryResult>(`/api/tiles/mine?layer=${layer}`),

  /** A látott térképszakasz birtokviszonya — mindenkié. */
  tiles: (
    layer: 'foot' | 'bike',
    view: { south: number; west: number; north: number; east: number },
  ) =>
    request<TilesResult>(
      `/api/tiles?layer=${layer}&south=${view.south}&west=${view.west}` +
        `&north=${view.north}&east=${view.east}`,
    ),

  /**
   * Az ÖSSZEFÜGGŐ TERÜLETFOLTOK a látott szakaszon.
   *
   * A `tiles`-tól eltérően ez NÉZETTŐL FÜGGETLEN, előszámolt egységeket ad:
   * egy folt akkor is teljes, ha kilóg a képernyőről, és ugyanaz a folt
   * ugyanakkora marad, akárhonnan nézzük. A méretszűrés a szerveren megy
   * (lásd `territoryScale.ts`), ezért a válasz mérete nem nő a nagyítással.
   */
  territoryBlobs: (
    layer: 'foot' | 'bike',
    view: { south: number; west: number; north: number; east: number },
  ) =>
    request<TerritoryBlobsResult>(
      `/api/tiles/blobs?layer=${layer}&south=${view.south}&west=${view.west}` +
        `&north=${view.north}&east=${view.east}`,
    ),

  /** My passport countries, newest first (own only — DECISIONS #101). */
  passport: () => request<{ countries: PassportCountry[] }>('/api/users/me/passport'),

  /** The stamp animation for these countries has been played. */
  markPassportSeen: (isos: readonly string[]) =>
    request<{ updated: number }>('/api/users/me/passport/seen', {
      method: 'POST',
      body: JSON.stringify({ isos }),
    }),

  /** Hourly snapshot of all blobs of the layer — the zoomed-out map draws from it. */
  territorySnapshot: (layer: 'foot' | 'bike') =>
    request<TerritorySnapshotResult>(`/api/tiles/snapshot?layer=${layer}`),

  /** My own blobs, live — laid over the snapshot so a fresh run shows at once. */
  myTerritoryBlobs: (layer: 'foot' | 'bike') =>
    request<{ layer: 'foot' | 'bike'; blobs: TerritoryBlob[] }>(`/api/tiles/blobs/mine?layer=${layer}`),

  /** Egy mező tulajdonosának kártyája — koppintásra kérjük le, nem előre. */
  tileOwner: (uid: string, layer: 'foot' | 'bike' = 'foot') =>
    request<{ owner: TileOwner }>(`/api/tiles/owner/${encodeURIComponent(uid)}?layer=${layer}`),

  /**
   * A ranglista. `window` szerint négy nézet: `alltime` a jelenlegi
   * területet mutatja, `day`/`week`/`month` a bruttó szerzést az adott
   * időszakban (a `gpWeek`/`gpMonth` mintájára — nem a nettó változást).
   */
  leaderboard: (layer: 'foot' | 'bike' = 'foot', window: LeaderboardWindow = 'alltime', after?: string) =>
    request<LeaderboardPage<LeaderboardEntry>>(
      `/api/tiles/leaderboard?layer=${layer}&window=${window}${after ? `&after=${encodeURIComponent(after)}` : ''}`,
    ),

  /**
   * Küldetés-ajánló. Időből saját/felülírt tempóval számol célhosszt, vagy
   * közvetlen kilométert fogad.
   */
  generateMissions: (input: {
    lat: number;
    lng: number;
    minutes?: number;
    distanceKm?: number;
    paceSecPerKm?: number;
    priority?: MissionPriority;
    /** Hány ajánlatot kérünk (1-5). Felső korlát, nem garancia. */
    limit?: number;
    preferredBearing?: number;
    type: ActivityType;
    /** Séta mozgásformánál nincs értelme — a felület akkor nem küldi. */
    routeCharacter?: RouteCharacter;
    preference?: RoutePreference;
    terrain?: TerrainPreference;
    preferCycleways?: boolean;
  }) =>
    request<MissionResult>('/api/missions/generate', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * A küldetés-ajánló GYORS fele: útvonalak terület és GP nélkül.
   *
   * Mérve (2026-08-29): 0,5–2,2 s, míg a teljes lánc nagy bringakörnél
   * 12,7 s. A kártya ebből már kirajzolható, a többi mező a
   * `missionsEvaluate` válaszával töltődik ki.
   */
  missionsPlan: (input: {
    lat: number;
    lng: number;
    minutes?: number;
    distanceKm?: number;
    paceSecPerKm?: number;
    priority?: MissionPriority;
    limit?: number;
    preferredBearing?: number;
    type: ActivityType;
    routeCharacter?: RouteCharacter;
    preference?: RoutePreference;
    terrain?: TerrainPreference;
    preferCycleways?: boolean;
  }) =>
    request<MissionPlanResult>('/api/missions/generate', {
      method: 'POST',
      body: JSON.stringify({ ...input, phase: 'plan' }),
    }),

  /**
   * A LASSÚ fele: a megtervezett útvonalakra terület, mező, GP és karakter.
   *
   * Nem fogyaszt kvótát — azt a `missionsPlan` már elszámolta.
   */
  missionsEvaluate: (input: {
    type: ActivityType;
    priority?: MissionPriority;
    limit?: number;
    routes: { polyline: string; bearing: number; maneuvers?: RouteManeuver[] }[];
  }) =>
    request<{ missions: Mission[]; reason?: string }>('/api/missions/evaluate', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * A→B útvonal tervezése.
   *
   * ⚠️ KVÓTÁS: ugyanabból a heti keretből fogy, mint a küldetés-generálás
   * (`FREE_ROUTE_GENERATIONS_PER_WEEK`), Pro előfizetéssel korlátlan. A válasz
   * `quotaLeft` mezője megmondja, mennyi maradt.
   *
   * ⚠️ LASSÚ: a tervezés mérve 3,6–7 másodperc, mert valódi útvonalakat
   * számol, nem egyenest húz. A felületnek ezt türelmesen kell kezelnie.
   */
  routesPlan: (input: RoutePlanInput) =>
    request<RoutePlanResult>('/api/routes/plan', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * Címkeresés a rajt/cél/megálló kijelöléséhez.
   *
   * A `near` a térkép közepe — a találatok TÁVOLSÁG szerint jönnek, nem a
   * szolgáltató relevanciája szerint (mérve: a „Blaha Lujza tér" budapesti
   * nézetből is Kiskunfélegyházát hozta elsőnek).
   *
   * ⚠️ Három karakter alatt a szerver nem is hív külső szolgáltatást — a
   * geocoding külön számlázódik.
   */
  routesGeocode: (query: string, near?: { lat: number; lng: number }) =>
    request<{ results: PlaceHit[] }>(
      `/api/routes/geocode?q=${encodeURIComponent(query)}` +
        (near ? `&near=${near.lng},${near.lat}` : ''),
    ),

  /**
   * „Grout” asszisztens: a mondatból a tervező beállításai (NEM tervez).
   *
   * ⚠️ Hozzájárulás nélkül 403 `assistant_consent_required` — ekkor a felület
   * a hozzájárulás-lapot mutatja. Napi keret: `ASSISTANT_REQUESTS_PER_DAY`.
   * A `near`-t a szerver ~1 km-re durvítja, és csak a helyfeloldáshoz használja.
   */
  routesInterpret: (text: string, near?: { lat: number; lng: number }) =>
    request<RouteInterpretResult>('/api/routes/interpret', {
      method: 'POST',
      body: JSON.stringify({ text, near }),
    }),

  assistantConsent: () =>
    request<{ ok: true; consentVersion: number }>('/api/assistant/consent', { method: 'POST' }),

  /** Visszavonás — a szerver a tárolt beszélgetéseket és a memóriát is törli (#79). */
  assistantConsentWithdraw: () =>
    request<{ ok: true }>('/api/assistant/consent', { method: 'DELETE' }),

  /**
   * „Grout” asszisztens: egy beszélgetés-forduló (#79). A szerver TÁROLJA a
   * beszélgetést, és a memóriát is frissíti. Napi keret: `ASSISTANT_REQUESTS_PER_DAY`.
   * Hozzájárulás nélkül 403 `assistant_consent_required`.
   */
  assistantMessage: (input: {
    conversationId: string | null;
    text: string;
    near?: { lat: number; lng: number };
    plan: AssistantPlanContext;
    /** A helyben lezajlott kérdés–válasz az előző üzenet óta (#80) — a szerver eltárolja. */
    localTurns?: { role: 'user' | 'assistant'; text: string }[];
    /** #86: the planner's chosen points — the park on the way is searched between them. */
    points?: { from?: { lat: number; lng: number }; to?: { lat: number; lng: number } };
    /** #87: the text is a choice made on the chat's buttons (already in the planner) — nothing to interpret. */
    choice?: true;
    /** #87: this client shows the model's free reply and quick replies (no fixed question chain). */
    modelReplies?: true;
  }) =>
    request<AssistantMessageResult>('/api/assistant/message', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  assistantConversations: () =>
    request<{ conversations: AssistantConversationSummary[] }>('/api/assistant/conversations'),

  assistantConversation: (id: string) =>
    request<{ id: string; title: string; messages: AssistantStoredMessage[] }>(
      `/api/assistant/conversations/${encodeURIComponent(id)}`,
    ),

  assistantDeleteConversation: (id: string) =>
    request<{ ok: true }>(`/api/assistant/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  assistantMemory: () => request<{ memory: AssistantMemoryView }>('/api/assistant/memory'),

  assistantForget: (kind: AssistantForgetKind, key = '') =>
    request<{ memory: AssistantMemoryView }>('/api/assistant/memory/forget', {
      method: 'POST',
      body: JSON.stringify({ kind, key }),
    }),

  /** A jelöltek közül választott hely — a szerver megjegyzi a névhez. */
  /** #86: a park between two points for the „Parkon át?” follow-up (no model call, no quota). */
  assistantGreenStop: (from: { lat: number; lng: number }, to: { lat: number; lng: number }) =>
    request<{ stop: PlaceHit | null }>('/api/assistant/green-stop', { method: 'POST', body: JSON.stringify({ from, to }) }),

  assistantRememberPlace: (input: { name: string; label: string; lat: number; lng: number }) =>
    request<{ ok: true }>('/api/assistant/places', { method: 'POST', body: JSON.stringify(input) }),

  /** Minden beszélgetés és a memória törlése; a hozzájárulás marad. */
  assistantDeleteHistory: () => request<{ ok: true }>('/api/assistant/history', { method: 'DELETE' }),

  otpSend: () => request<OtpSendResult>('/api/auth/otp/send', { method: 'POST' }),

  emailChange: (email: string) =>
    request<EmailChangeResult>('/api/auth/email/change', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  otpVerify: (code: string) =>
    request<{ verified: true }>('/api/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /** Az egységes moderációs sor (#91): ellenőrzés, fellebbezés, bejelentés — határidő szerint. */
  moderationQueue: () => request<{ items: ModerationQueueItem[] }>('/api/dev/moderation-queue'),

  /**
   * `pending`: a mért verdikt szerint nem érvényes, döntésre váró aktivitások
   * (terv 2.1); `decided`: ezek közül a már eldöntöttek (#91).
   */
  devActivities: (cursor?: string, view: DevActivityView = 'all') =>
    request<{ activities: DevActivityListItem[]; nextCursor: string | null }>(
      `/api/dev/activities?limit=30${view === 'pending' ? '&review=1' : view === 'decided' ? '&review=decided' : ''}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),

  /**
   * Moderátori döntés és a játékhatása (terv 2.2, #66): visszatartott aktivitás
   * jóváhagyáskor könyvelődik (`applied`), elutasításkor „Elutasítva" lesz
   * (`rejected`); már könyveltnél csak rögzül (`recorded`).
   */
  devActivityDecision: (id: string, verdict: DevActivityDecision['verdict'], note?: string) =>
    request<{
      ok: true;
      effect: 'applied' | 'rejected' | 'recorded';
      resume: { outcome: string; summary: { gp?: number; areaGainedM2?: number } | null } | null;
    }>(`/api/dev/activities/${encodeURIComponent(id)}/decision`, {
      method: 'POST',
      body: JSON.stringify({ verdict, note }),
    }),

  devActivity: (id: string) =>
    request<{
      activity: DevActivityDetail;
      trust: DevActivityTrust | null;
      points: Array<{ lat: number; lng: number; t: number; accuracy?: number; elevation?: number }>;
      audit: DevActivityAudit | null;
      lifecycle: DevActivityLifecycleEvent[];
      /** Régebbi rögzítésnél hiányzik. */
      trace?: DevActivityTraceEvent[];
      perf?: import('./perfMeter').PerfSummaryRow[];
      /** Trimmed before Save; `null`/missing = not trimmed (older servers omit it). */
      trim?: import('@/tracking/recorder').TrimSummary | null;
    }>(`/api/dev/activities/${encodeURIComponent(id)}`),

  /**
   * Szabálymagyarázó — nyilvános, hitelesítés nélkül is hívható.
   * Ugyanabból a sémából jön, mint az admin szerkesztő, ezért egy átállított
   * szorzó után a szöveg sem hazudik.
   */
  rules: () => request<RulesState>('/api/rules'),

  // ── Admin ───────────────────────────────────────────────────────────────
  adminStatus: () => request<AdminStatus>('/api/admin/status'),
  socialAdminStatus: () => request<SocialAdminStatus>('/api/social-admin/status'),
  socialAdminStartConnection: (provider: SocialOAuthProvider) =>
    request<{ url: string }>(`/api/social-admin/connections/${provider}/start`, { method: 'POST' }),
  socialAdminDisconnect: (provider: SocialOAuthProvider) =>
    request<{ ok: true }>(`/api/social-admin/connections/${provider}`, { method: 'DELETE' }),
  socialAdminRemoveWizard: (id: string) => request<{ ok: true }>(`/api/social-admin/content-engine/wizard/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  socialAdminWizardPosts: (id: string) => request<{ posts: SocialPost[] }>(`/api/social-admin/content-engine/wizard/${encodeURIComponent(id)}/posts`),
  socialAdminRemovePlan: (id: string) => request<{ ok: true }>(`/api/social-admin/content-engine/planning-runs/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  socialAdminPostAction: (id: string, action: 'unschedule' | 'discard' | 'manual', publicationUrl?: string) => request<{ post: SocialPost }>(`/api/social-admin/posts/${encodeURIComponent(id)}/lifecycle`, { method: 'POST', body: JSON.stringify({ action, publicationUrl }) }),
  socialAdminDownloadMedia: (id: string) => requestBlob(`/api/social-admin/posts/${encodeURIComponent(id)}/media`),
  socialAdminWizardProgress: (id: string, step: number) => request<{ draft: WizardDraft }>(`/api/social-admin/content-engine/wizard/${encodeURIComponent(id)}/progress`, { method: 'POST', body: JSON.stringify({ step }) }),
  socialAdminWizardDrafts: () => request<{ drafts: WizardDraft[] }>('/api/social-admin/content-engine/wizard'),
  socialAdminSaveWizard: (id: string, input: { version: number; step: number; data: WizardData }) => request<{ draft: WizardDraft }>(`/api/social-admin/content-engine/wizard/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(input) }),
  socialAdminSuggestWizard: (input: { step: WizardStep; field: string | null; prompt: string; data: WizardData }) => request<{ values: Record<string, WizardValue> }>('/api/social-admin/content-engine/wizard/suggest', { method: 'POST', body: JSON.stringify(input) }),
  socialAdminCreateWizard: (id: string, input: { version: number; basis: NarrativeBasis; confirmed: true }) => request<{ draft: WizardDraft }>(`/api/social-admin/content-engine/wizard/${encodeURIComponent(id)}/create`, { method: 'POST', body: JSON.stringify(input) }),
  socialAdminPosts: (from: string, to: string) =>
    request<{ posts: SocialPost[] }>(`/api/social-admin/posts?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  socialAdminContentImportStatus: () => request<ContentEngineImportStatus>('/api/social-admin/content-engine/imports/status'),
  socialAdminContentLibrary: () => request<ContentEngineLibrary>('/api/social-admin/content-engine/library'),
  socialAdminContentNarrative: () => request<NarrativeView>('/api/social-admin/content-engine/narrative'),
  socialAdminPlanningSetup: () => request<PlanningSetup>('/api/social-admin/content-engine/planning'),
  socialAdminCreatePlanningRun: (input: PlanningRequest) => request<PlanningRun>('/api/social-admin/content-engine/planning-runs', { method: 'POST', body: JSON.stringify(input) }),
  socialAdminPlanningRun: (id: string) => request<PlanningRun>(`/api/social-admin/content-engine/planning-runs/${encodeURIComponent(id)}`),
  socialAdminCreateProductionBrief: (input: ProductionBriefRequest) => request<{ brief: ProductionBrief }>('/api/social-admin/content-engine/production-briefs', { method: 'POST', body: JSON.stringify(input) }),
  socialAdminCreateProductionDraft: (id: string, input: ProductionDraftRequest) => request<ProductionDraftResult>(`/api/social-admin/content-engine/production-briefs/${encodeURIComponent(id)}/drafts`, { method: 'POST', body: JSON.stringify(input) }),
  socialAdminSaveNarrative: (id: string, input: NarrativeSave) =>
    request<{ record: NarrativeVersion; basis: NarrativeView['basis'] }>(`/api/social-admin/content-engine/narrative/${input.data.type}/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(input) }),
  socialAdminApproveNarrative: (kind: NarrativeKind, id: string, input: NarrativeApprove) =>
    request<{ approval: NarrativeApproval }>(`/api/social-admin/content-engine/narrative/${kind}/${encodeURIComponent(id)}/approve`, { method: 'POST', body: JSON.stringify(input) }),
  socialAdminNarrativeHistory: (kind: NarrativeKind, id: string) =>
    request<NarrativeHistory>(`/api/social-admin/content-engine/narrative/${kind}/${encodeURIComponent(id)}/history`),
  socialAdminContentKnowledge: (platform: KnowledgeContext['platform'], region: string) =>
    request<ContentEngineKnowledge>(`/api/social-admin/content-engine/knowledge?platform=${encodeURIComponent(platform)}&region=${encodeURIComponent(region)}`),
  socialAdminSaveKnowledge: (id: string, data: KnowledgeData, versionId: string | null, basis: ContentEngineKnowledge['basis']) =>
    request<{ record: KnowledgeRecord; basis: ContentEngineKnowledge['basis'] }>(`/api/social-admin/content-engine/knowledge/${data.type}/${encodeURIComponent(id)}`, {
      method: 'PUT', body: JSON.stringify({ data, versionId, basis }),
    }),
  socialAdminVerifyKnowledge: (kind: KnowledgeKind, id: string, versionId: string, basis: ContentEngineKnowledge['basis']) =>
    request<{ record: KnowledgeRecord; basis: ContentEngineKnowledge['basis'] }>(`/api/social-admin/content-engine/knowledge/${kind}/${encodeURIComponent(id)}/verify`, {
      method: 'POST', body: JSON.stringify({ basis, versionId }),
    }),
  socialAdminReviewKnowledgeMessage: (message: ContentEngineKnowledge['messages'][number], claimFree: boolean, view: ContentEngineKnowledge) =>
    request<{ reviewed: true }>(`/api/social-admin/content-engine/knowledge/messages/${encodeURIComponent(message.key)}/review`, {
      method: 'POST', body: JSON.stringify({ basis: view.basis, hash: message.hash, claimFree, platform: view.context.platform, region: view.context.region }),
    }),
  socialAdminEditContentEntry: (entry: ContentEngineLibraryEntry, basis: ContentEngineLibrary['basis'], fields: Record<string, string>) =>
    request<{ basis: ContentEngineLibrary['basis']; entry: ContentEngineLibraryEntry }>(`/api/social-admin/content-engine/library/entries/${encodeURIComponent(entry.key)}`, {
      method: 'PATCH', body: JSON.stringify({ basis, versionId: entry.versionId, fields }),
    }),
  socialAdminApproveContentGroup: (scope: ContentEngineLibraryScope, hash: string, basis: ContentEngineLibrary['basis']) =>
    request<{ hash: string; approved: true }>(`/api/social-admin/content-engine/library/groups/${scope}/approve`, {
      method: 'POST', body: JSON.stringify({ hash, basis }),
    }),
  socialAdminPreviewContentDatabase: (fileName: string, contentBase64: string) =>
    request<{ preview: ContentEngineImportPreview }>('/api/social-admin/content-engine/imports/preview', {
      method: 'POST',
      body: JSON.stringify({ fileName, contentBase64 }),
    }),
  socialAdminStageContentDatabase: (fileName: string, contentBase64: string) =>
    request<{ staging: ContentEngineImportStaging; created: boolean }>('/api/social-admin/content-engine/imports/stage', {
      method: 'POST',
      body: JSON.stringify({ fileName, contentBase64 }),
    }),
  socialAdminContentImportDiff: (id: string, aliases: Record<string, string> = {}) =>
    request<{ diff: ContentEngineImportDiff }>(`/api/social-admin/content-engine/imports/${encodeURIComponent(id)}/diff`, {
      method: 'POST', body: JSON.stringify({ aliases }),
    }),
  socialAdminApproveContentImport: (id: string, review: ContentEngineReviewInput) =>
    request<{ approval: ContentEngineImportApproval }>(`/api/social-admin/content-engine/imports/${encodeURIComponent(id)}/approve`, {
      method: 'POST', body: JSON.stringify(review),
    }),
  socialAdminActivateContentImport: (id: string) =>
    request<{ activation: ContentEngineImportActivation }>(`/api/social-admin/content-engine/reviews/${encodeURIComponent(id)}/activate`, { method: 'POST' }),
  socialAdminGenerateCalendar: (startDate: string, days = 7) =>
    request<{ created: number; existing: number; rescheduled: number; filled: number; startDate: string; endDate: string }>('/api/social-admin/calendar/generate', {
      method: 'POST',
      body: JSON.stringify({ startDate, days }),
    }),
  socialAdminUpdatePost: (
    id: string,
    changes: Partial<Pick<SocialPost, 'title' | 'caption' | 'overlayText' | 'visualPrompt' | 'scheduledDate' | 'scheduledTime' | 'pillar' | 'format' | 'visualTheme' | 'landingUrl'>>,
  ) => request<{ post: SocialPost }>(`/api/social-admin/posts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(changes),
  }),
  socialAdminUploadImage: (id: string, input: { base64?: string; url?: string; title: string; overlayText: string; visualTheme: SocialVisualTheme }) =>
    request<{ post: SocialPost }>(`/api/social-admin/posts/${encodeURIComponent(id)}/upload-image`, { method: 'POST', body: JSON.stringify(input) }),
  socialAdminGenerateCopy: (id: string) =>
    request<{ post: SocialPost }>(`/api/social-admin/posts/${encodeURIComponent(id)}/generate-copy`, { method: 'POST' }),
  socialAdminGenerateImage: (id: string, changes: Pick<SocialPost, 'title' | 'overlayText' | 'visualPrompt' | 'visualTheme'>) =>
    request<{ post: SocialPost }>(`/api/social-admin/posts/${encodeURIComponent(id)}/generate-image`, {
      method: 'POST',
      body: JSON.stringify(changes),
    }),
  socialAdminGenerateAdobeImage: (id: string, changes: Pick<SocialPost, 'title' | 'overlayText' | 'visualPrompt' | 'visualTheme'>) =>
    request<{ post: SocialPost }>(`/api/social-admin/posts/${encodeURIComponent(id)}/generate-adobe-image`, {
      method: 'POST',
      body: JSON.stringify(changes),
    }),
  socialAdminGenerateVideo: (id: string, visualPrompt: string) =>
    request<{ post: SocialPost }>(`/api/social-admin/posts/${encodeURIComponent(id)}/generate-video`, {
      method: 'POST',
      body: JSON.stringify({ visualPrompt }),
    }),
  socialAdminApprovePost: (id: string, now = false) =>
    request<{ post: SocialPost }>(`/api/social-admin/posts/${encodeURIComponent(id)}/approve`, { method: 'POST', body: JSON.stringify({ now }) }),

  adminMetrics: (days = 14) => request<AdminMetrics>(`/api/admin/metrics?days=${days}`),

  adminUsage: (range?: { fromDay: number; toDay: number }) =>
    request<AdminUsageOverview>(
      range ? `/api/admin/usage?from=${range.fromDay}&to=${range.toDay}` : '/api/admin/usage',
    ),

  appUsageHeartbeat: (input: { sessionId: string; totalActiveMs: number; platform: string }) =>
    request<{ ok: true; acceptedMs: number }>('/api/usage/heartbeat', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * Teszt-értesítés a saját eszközökre, eszközönkénti nyers FCM hibakóddal.
   * A push csendben hasal el; enélkül csak a Cloud Run naplójából derülne ki,
   * miért nem érkezik meg (lásd `server/src/routes/admin.ts` → `/push/test`).
   */
  adminTestPush: () => request<AdminPushTest>('/api/admin/push/test', { method: 'POST' }),

  /**
   * A `bandas/{id}.totals` előszámítását kézzel indítja — addig kell, amíg
   * nincs bekötve a Cloud Scheduler (`docs/06-architektura-es-admin.md` →
   * `jobs` → `banda-rollover`). A végpont `owner`/`admin` szerepkört fogad
   * el ID-tokennel, ugyanúgy, mint az ütemező a megosztott titkot.
   */
  adminRunBandaRollover: () => request<BandaRolloverResult>('/api/jobs/banda-rollover', { method: 'POST' }),

  /** A beragadt mentések söprögetője kézzel (GRUNDO #61) — ugyanaz, amit az ütemező 15 percenként. */
  adminRunResumeStuck: () => request<ResumeStuckResult>('/api/jobs/resume-stuck', { method: 'POST' }),

  /**
   * Egy mentett főszál-mérés feltöltése. A dokumentum azonosítója a mérésé,
   * ezért az ismételt küldés felülír, nem duplikál — a telefon offline is
   * menthet, és a feltöltés később pótolható.
   */
  adminUploadPerfSnapshot: (entry: PerfHistoryEntry) =>
    request<{ id: string; stats: number }>('/api/admin/perf-snapshots', {
      method: 'POST',
      body: JSON.stringify(entry),
    }),

  adminPerfSnapshots: (limit = 50) =>
    request<{ entries: ServerPerfSnapshot[] }>(`/api/admin/perf-snapshots?limit=${limit}`),

  adminGameplay: () => request<GameplayState>('/api/admin/gameplay'),

  /**
   * A TELJES felülírás-halmazt küldjük, nem különbséget.
   *
   * Amit kihagyunk, az visszaáll alapértékre — így a felület állapota és a
   * tárolt állapot nem tud szétcsúszni.
   */
  adminSaveGameplay: (overrides: Record<string, number | boolean>, note: string) =>
    request<GameplayState>('/api/admin/gameplay', {
      method: 'PUT',
      body: JSON.stringify({ overrides, note }),
    }),

  adminGameplayVersions: () =>
    request<{ versions: GameplayVersion[] }>('/api/admin/gameplay/versions'),

  /**
   * A `CHANGELOG.md` szinkronizált tükre — lásd `scripts/sync-changelog.mjs`
   * és `.claude/rules/versioning.md`.
   */
  /** A `/live/battle` játékoslistája — lásd `server/src/routes/liveBattle.ts`. */
  adminLiveBattle: () => request<{ players: LiveBattlePlayer[] }>('/api/admin/live-battle'),

  adminSaveLiveBattle: (usernames: string[]) =>
    request<{ players: LiveBattlePlayer[] }>('/api/admin/live-battle', {
      method: 'PUT',
      body: JSON.stringify({ usernames }),
    }),

  adminChangelog: (limit = 50) =>
    request<{ entries: ChangelogEntry[] }>(`/api/admin/changelog?limit=${limit}`),

  adminRollbackGameplay: (version: number) =>
    request<GameplayState>('/api/admin/gameplay/rollback', {
      method: 'POST',
      body: JSON.stringify({ version }),
    }),

  adminModifiers: (includeExpired = false) =>
    request<{ modifiers: AdminModifier[] }>(
      `/api/admin/modifiers${includeExpired ? '?expired=1' : ''}`,
    ),

  adminCreateModifier: (input: CreateModifierInput) =>
    request<{ id: string }>('/api/admin/modifiers', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  adminUpdateModifier: (id: string, input: CreateModifierInput) =>
    request<{ ok: true }>(`/api/admin/modifiers/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  adminCancelModifier: (id: string) =>
    request<{ ok: true }>(`/api/admin/modifiers/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
    }),

  adminDeleteModifier: (id: string) =>
    request<{ ok: true }>(`/api/admin/modifiers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // ── Bugreport ───────────────────────────────────────────────────────────

  /**
   * Hibabejelentés beküldése. A válasz `uploadPrefix`-e az EGYETLEN hely, ahová
   * ehhez a bejelentéshez melléklet tölthető — a `storage.rules` és a szerver
   * ugyanezt az előtagot kényszeríti ki.
   */
  submitBugReport: (input: BugReportInput) =>
    request<BugReportCreated>('/api/bugreports', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** A már feltöltött melléklet hozzákötése a bejelentéshez. */
  attachBugReportMedia: (reportId: string, media: BugReportMediaInput) =>
    request<{ ok: true }>(`/api/bugreports/${encodeURIComponent(reportId)}/media`, {
      method: 'POST',
      body: JSON.stringify(media),
    }),

  adminBugReports: (filter: { status?: string; kind?: string } = {}) => {
    const params = new URLSearchParams();
    if (filter.status) params.set('status', filter.status);
    if (filter.kind) params.set('kind', filter.kind);
    const query = params.toString();
    return request<{ reports: AdminBugReportListItem[] }>(
      `/api/admin/bugreports${query ? `?${query}` : ''}`,
    );
  },

  adminBugReport: (id: string) =>
    request<{ report: AdminBugReportDetail }>(`/api/admin/bugreports/${encodeURIComponent(id)}`),

  adminUpdateBugReport: (id: string, patch: AdminBugReportPatch) =>
    request<{ ok: true }>(`/api/admin/bugreports/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /** Felhasználói/aktivitás-bejelentések triázsa (terv 1.4, #65). */
  adminReports: (status: 'open' | 'upheld' | 'dismissed' | '' = 'open') =>
    request<{ reports: AdminReportListItem[] }>(
      `/api/admin/reports${status ? `?status=${status}` : ''}`,
    ),

  adminResolveReport: (id: string, decision: 'upheld' | 'dismissed', resolution?: string) =>
    request<{ ok: true }>(`/api/admin/reports/${encodeURIComponent(id)}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ decision, resolution }),
    }),

  /** Bejelentett banda jelenlegi állapota a kézi moderáláshoz. */
  adminBanda: (bandaId: string) =>
    request<{ banda: AdminBandaDetail }>(`/api/admin/bandas/${encodeURIComponent(bandaId)}`),

  /** Név/leírás/város cseréje, képek levétele — indoklással, naplózva. */
  adminModerateBanda: (
    bandaId: string,
    input: { name?: string; description?: string | null; city?: string | null; clearPhoto?: boolean; clearCover?: boolean; reason: string },
  ) =>
    request<{ banda: Pick<AdminBandaDetail, 'name' | 'description' | 'city' | 'photoURL' | 'coverURL'> }>(
      `/api/admin/bandas/${encodeURIComponent(bandaId)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    ),

  /** Banda feloszlatása moderátori döntésre — indoklással, naplózva. */
  adminDissolveBanda: (bandaId: string, reason: string) =>
    request<{ dissolved: true }>(`/api/admin/bandas/${encodeURIComponent(bandaId)}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),

  /** A jelentett DM-kép bizonyítékmásolata (#68) — csak triázs-szerepkörnek. */
  adminReportEvidence: (id: string, signal?: AbortSignal) =>
    requestBlob(`/api/admin/reports/${encodeURIComponent(id)}/evidence`, signal),

  /** A jelentett privát üzenet szerkesztés/törlés előtti szövegei (2026-11-04, 30 napig) — triázs. */
  adminReportMessageRevisions: (id: string) =>
    request<{ revisions: AdminMessageRevision[] }>(`/api/admin/reports/${encodeURIComponent(id)}/message-revisions`),

  /** „GRUNDO csapat" közös postafiók (#68) — owner/admin. */
  adminTeamInbox: (filter: 'needsReply' | 'all') =>
    request<{ items: AdminTeamInboxItem[] }>(`/api/admin/team-messages?filter=${filter}`),

  adminTeamThread: (uid: string) =>
    request<AdminTeamThread>(`/api/admin/team-messages/${encodeURIComponent(uid)}`),

  adminSendTeamMessage: (uid: string, text: string, replyToId?: string) =>
    request<{ conversationId: string; messageId: string }>(`/api/admin/team-messages/${encodeURIComponent(uid)}`, {
      method: 'POST',
      body: JSON.stringify({ text, ...(replyToId ? { replyToId } : {}) }),
    }),

  /** Admin — teljes, lapozott felhasználólista (#67). `usernameLower` szerint rendezve. */
  adminUsers: (params: { limit: 25 | 50 | 100; cursor?: string | null }) =>
    request<{ items: AdminUserSummary[]; nextCursor: string | null }>(
      `/api/admin/users?limit=${params.limit}${params.cursor ? `&cursor=${encodeURIComponent(params.cursor)}` : ''}`,
    ),

  /** A TELJES admin felhasználólista Auth-adatokkal — a szűrés és a lapozás a böngészőben fut. */
  adminAllUsers: () => request<{ items: AdminUserSummary[]; truncated: boolean }>('/api/admin/users/all'),

  /** Admin felhasználókeresés — név (előtag), pontos e-mail vagy pontos UID. */
  adminUserSearch: (q: string) =>
    request<{ items: AdminUserSummary[] }>(`/api/admin/users/search?q=${encodeURIComponent(q)}`),

  /** Az aktív tesztelői jogosultsággal rendelkező felhasználók. */
  adminTesters: () => request<{ items: AdminTesterSummary[] }>('/api/admin/testers'),

  adminUserDetail: (uid: string) =>
    request<AdminUserDetail>(`/api/admin/users/${encodeURIComponent(uid)}`),

  adminBanUser: (uid: string, reason: string) =>
    request<{ banned: true; hidden: unknown }>(`/api/admin/users/${encodeURIComponent(uid)}/ban`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  adminUnbanUser: (uid: string) =>
    request<{ unbanned: true; restored: unknown }>(`/api/admin/users/${encodeURIComponent(uid)}/unban`, {
      method: 'POST',
    }),

  adminShadowbanUser: (uid: string, reason: string) =>
    request<{ shadowbanned: true }>(`/api/admin/users/${encodeURIComponent(uid)}/shadowban`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /** Területkorrekció: a felhasználó TELJES területe felszabadul (terv 2.2, #66). Nem visszafordítható. */
  adminRevokeTerritory: (uid: string, reason: string) =>
    request<{ released: { foot: number; bike: number } }>(
      `/api/admin/users/${encodeURIComponent(uid)}/revoke-territory`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  adminUnshadowbanUser: (uid: string) =>
    request<{ unshadowbanned: true }>(`/api/admin/users/${encodeURIComponent(uid)}/unshadowban`, {
      method: 'POST',
    }),

  /** Tesztelői jog ki-be kapcsolása felhasználónév alapján. */
  adminSetTester: (username: string, tester: boolean) =>
    request<{ uid: string; username: string; tester: boolean }>('/api/admin/testers', {
      method: 'POST',
      body: JSON.stringify({ username, tester }),
    }),
};
